// --- temp-only reaction merge diagnostic banner tracking ---
const __warnedTempOnly = new Set();
// --- socket URL resolution -------------------------------------------------
function __try(v){ return (v && typeof v==='string' && v.trim()) || ''; }
function resolveSocketUrl(baseUrl){
  // 1) explicit argument wins
  let url = __try(baseUrl);
  let source = 'arg';
  // 2) window override
  if(!url && typeof window!=='undefined') { url = __try(window.__SOCKET_URL__); source='window.__SOCKET_URL__'; }
  // 3) Vite/CRA envs
  if(!url && typeof import.meta!=='undefined' && import.meta.env){
    url = __try(import.meta.env.VITE_SOCKET_URL) || __try(import.meta.env.VITE_API_URL);
    if(url) source = 'import.meta.env';
  }
  if(!url && typeof process!=='undefined' && process.env){
    url = __try(process.env.REACT_APP_SOCKET_URL) || __try(process.env.REACT_APP_API_URL);
    if(url) source = 'process.env';
  }
  // 4) Dev heuristic: frontend :7072 -> backend :7071
  if(!url && typeof window!=='undefined' && window.location){
    try{
      const u = new URL(window.location.origin);
      if(u.port){
        const p = parseInt(u.port,10);
        if(!Number.isNaN(p) && p>0){ u.port = String(p - 1); url = u.origin; source = 'heuristic port-1'; }
      }
    }catch{}
  }
  // 5) Fallback to current origin
  if(!url && typeof window!=='undefined' && window.location){ url = window.location.origin; source = 'window.origin fallback'; }
  return { url, source };
}
// --------------------------------------------------------------------------
import { create } from 'zustand';
import { io } from 'socket.io-client';
import * as conciergeApi from '../api/conciergeApi';

// --- tiny throttle util for viewport handlers ---
function throttle(fn, ms = 800) {
  let t = 0;
  return (...args) => {
    const now = Date.now();
    if (now - t < ms) return;
    t = now;
    return fn(...args);
  };
}
// --- presence timing knobs (Telegram-like) ---
const HEARTBEAT_MS = 12_000;     // staff ping while active
const ONLINE_WINDOW_MS = 20_000; // 20s feels snappy, Telegram-like // flip to "last seen" quickly when no fresh ping

// --- id helpers -----------------------------------------------------------
const toIdString = (v) => {
  if (v && typeof v === 'object') {
    if (v._id) return String(v._id);
    // Mongo ObjectId sent as plain object? try common keys
    if (v.$oid) return String(v.$oid);
  }
  return v != null ? String(v) : '';
};
const samePropId = (a, b) => toIdString(a) === toIdString(b);
// -------------------------------------------------------------------------

// --- thread normalizer: ensure email and stable ids for UI ---
function normalizeThread(t) {
  if (!t) return t;
  const n = { ...t };
  // normalize propertyId to a flat string for comparisons and room keys
  if (n.propertyId) n.propertyId = toIdString(n.propertyId);
  // prefer backend-provided email; mirror to guestEmail if missing so UI can use one field
  if (!n.guestEmail && n.email) n.guestEmail = n.email;
  // surface a stable id for React keys
  if (!n.id && (n._id || n.threadId)) n.id = n._id || n.threadId;
  // Include last active timestamps
  n.guestLastActiveAt = t.guestLastActiveAt ? new Date(t.guestLastActiveAt) : null;
  n.staffLastActiveAt = t.staffLastActiveAt ? new Date(t.staffLastActiveAt) : null;
  return n;
}

// --- message identity helpers & de-dupe ---
function msgId(m) {
  return (m && (m.id || m._id || m.tempId)) || null;
}
function withStableId(m) {
  if (!m) return m;
  if (m.id) return m;
  if (m._id) return { ...m, id: m._id };
  if (m.tempId) return { ...m, id: m.tempId };
  return m;
}
function dedupeMessages(list) {
  const seen = new Set();
  const out = [];
  for (const raw of list || []) {
    const m = withStableId(raw);
    const k = msgId(m);
    if (k && seen.has(k)) continue;
    if (k) seen.add(k);
    out.push(m);
  }
  return out;
}

function pickSample(arr, n = 2) {
  if (!Array.isArray(arr)) return [];
  return arr.slice(0, Math.min(n, arr.length));
}
function fmtMsg(m) {
  if (!m) return m;
  const id = m.id || m._id || m.tempId;
  const at = m.createdAt || m.timestamp;
  return { id, sender: m.sender, at, text: m.text || m.content, seen: m.seen };
}

function normalizeIncoming(raw) {
  if (!raw) return raw;
  const m = withStableId(raw);
  const createdAt = m.createdAt || m.timestamp || new Date().toISOString();
  const text = m.text ?? m.content ?? '';
  // surface reply metadata
  const out = { ...m, createdAt, text, replyTo: m.replyTo, replySnapshot: m.replySnapshot, editedAt: m.editedAt };
  try {
    console.debug('[conciergeStore] normalizeIncoming ->', { ...fmtMsg(out), hasReply: !!(out.replyTo || out.replySnapshot) });
  } catch {}
  return out;
}

// Ensure chronological order (oldest -> newest) so latest sits at the bottom
function sortChronoAsc(list) {
  return [...(list || [])].sort((a, b) => {
    const ta = new Date(a.createdAt || a.timestamp || 0).getTime();
    const tb = new Date(b.createdAt || b.timestamp || 0).getTime();
    return ta - tb;
  });
}

// --- seen/read helpers ---------------------------------------------------
function lastGuestMessageIndex(list){
  if(!Array.isArray(list) || !list.length) return -1;
  for(let i=list.length-1;i>=0;i--){
    const m = list[i];
    if (senderRoleOf(m) === 'guest') return i;
  }
  return -1;
}
function staffHasSeen(m){
  if (!m) return false;
  const sb = Array.isArray(m.seenBy) ? m.seenBy : [];
  return sb.some(s => s && s.role === 'staff');
}

// --- reactions helpers ---------------------------------------------------
// Actor constants
// --- reactions helpers ---------------------------------------------------
// The concierge app acts as a STAFF actor (role-based, not legacy 'hotel')
const CURRENT_ACTOR = 'staff';

// Resolve current staff actor (always include a stable id when possible)
function resolveCurrentActor(getFn) {
  try {
    const st = (typeof getFn === 'function') ? getFn() : null;
    const me = st && st.me;
    // 1) Prefer authenticated staff id from store
    if (me && (me._id || me.id)) {
      return { role: CURRENT_ACTOR, id: String(me._id || me.id), name: me.fullName || me.name || me.username };
    }
    // 2) Global app user object (set by App.js) — primary fallback
    if (typeof window !== 'undefined' && window.__EF_USER) {
      const u = window.__EF_USER;
      if (u && (u._id || u.id)) {
        return { role: CURRENT_ACTOR, id: String(u._id || u.id), name: u.username || u.displayName || u.name };
      }
    }
    // 3) Legacy globals used elsewhere in the app
    if (typeof window !== 'undefined') {
      const wid = window.__STAFF_ID__ || (window.__STAFF__ && (window.__STAFF__._id || window.__STAFF__.id));
      if (wid) return { role: CURRENT_ACTOR, id: String(wid) };
    }
    // 4) LocalStorage hints as a last strong hint before fallback
    if (typeof localStorage !== 'undefined') {
      const lid = localStorage.getItem('staffId') || localStorage.getItem('userId');
      if (lid) return { role: CURRENT_ACTOR, id: String(lid) };
    }
    // 5) Final generic fallback (keeps role for legacy checks)
    return { role: CURRENT_ACTOR, id: 'staff:concierge' };
  } catch {
    return { role: CURRENT_ACTOR, id: 'staff:concierge' };
  }
}
// Normalize a list of actors coming from various shapes
function normalizeActors(raw){
  if (Array.isArray(raw)) return raw.filter(Boolean).map(a => ({ id: String(a.id), role: a.role }));
  if (raw && Array.isArray(raw.by)) return raw.by.filter(Boolean).map(a => ({ id: String(a.id), role: a.role }));
  return [];
}

// Convert a reactions map back to the array shape the UI expects
function mapToArray(map){
  const out = [];
  for (const [emoji, v] of Object.entries(map || {})) {
    const actors = Array.isArray(v.actors) ? v.actors.map(a => ({ id: String(a.id), role: a.role })) : [];
    const hasFlags = (typeof v.hotel === 'boolean') || (typeof v.guest === 'boolean');
    const count = actors.length || (hasFlags ? ((v.hotel ? 1 : 0) + (v.guest ? 1 : 0)) : (Number(v.count) || 0));
    out.push({ emoji, guest: !!v.guest, hotel: !!v.hotel, count, actors });
  }
  return out;
}

// Reconcile reactions array in-place style (preserve order/keys to avoid flicker)
function upsertReactionArray(prevArr, emoji, updatedEntry) {
  const prev = Array.isArray(prevArr) ? prevArr.slice() : [];
  const idx = prev.findIndex(r => r && r.emoji === emoji);

  // consider "empty" -> removal
  const isEmpty =
    !updatedEntry ||
    ((!updatedEntry.hotel && !updatedEntry.guest) &&
     (!Array.isArray(updatedEntry.actors) || updatedEntry.actors.length === 0) &&
     (!Number(updatedEntry.count) || Number(updatedEntry.count) <= 0));

  if (isEmpty) {
    if (idx !== -1) prev.splice(idx, 1);
    return prev;
  }

  const nextItem = {
    emoji,
    guest: !!updatedEntry.guest,
    hotel: !!updatedEntry.hotel,
    count: computeCount(updatedEntry),
    actors: Array.isArray(updatedEntry.actors)
      ? updatedEntry.actors.map(a => ({ id: String(a.id), role: a.role }))
      : []
  };

  if (idx === -1) prev.push(nextItem);
  else prev[idx] = nextItem;

  return prev;
}

// Map a role or actor object to legacy reaction key used in current UI ('guest'|'hotel')
function roleKey(actorOrRole) {
  const role = (actorOrRole && typeof actorOrRole === 'object') ? actorOrRole.role : actorOrRole;
  return role === 'guest' ? 'guest' : 'hotel'; // staff/bot/system -> hotel bucket
}

// Extract sender role from either a full message or a sender value
function senderRoleOf(input) {
  const s = (input && input.sender !== undefined) ? input.sender : input;
  if (!s) return undefined;
  if (typeof s === 'object') return s.role;
  if (s === 'guest') return 'guest';
  if (s === 'hotel') return 'staff';
  return undefined;
}

// Compare two sender values (object or legacy string). If both have ids, compare ids; else compare roles.
function sameSender(a, b) {
  const ra = senderRoleOf(a);
  const rb = senderRoleOf(b);
  if (ra !== rb) return false;
  const ia = (a && typeof a === 'object' && a.id) ? String(a.id) : null;
  const ib = (b && typeof b === 'object' && b.id) ? String(b.id) : null;
  if (ia && ib) return ia === ib;
  return true;
}
// Normalize reactions from various shapes into a canonical map:
// { [emoji]: { hotel: boolean, guest: boolean, count: number } }
function normalizeReactionsMap(raw) {
  if (!raw) return {};
  // Already a map?
  if (!Array.isArray(raw) && typeof raw === 'object') {
    const out = {};
    for (const [emoji, v] of Object.entries(raw)) {
      const hotel = !!(v.hotel || v.byHotel);
      const guest = !!(v.guest || v.byGuest);
      // If actor flags are present, always derive count from flags to avoid stale numeric counts
      const hasFlags = (typeof v.hotel === 'boolean') || (typeof v.guest === 'boolean') || (typeof v.byHotel === 'boolean') || (typeof v.byGuest === 'boolean');
      const count = hasFlags ? ((hotel ? 1 : 0) + (guest ? 1 : 0)) : (Number(v.count) || 0);
      const actors = normalizeActors(v);
      out[emoji] = { hotel, guest, count, actors };
    }
    return out;
  }
  // Array snapshot (legacy or server snapshot):
  // Accept shapes like [{ emoji, count, hotel, guest }] and preserve booleans when provided.
  if (Array.isArray(raw)) {
    const out = {};
    for (const r of raw) {
      const e = r.emoji || r.key || r.id;
      if (!e) continue;
      const hotel = !!(r.hotel || r.byHotel);
      const guest = !!(r.guest || r.byGuest);
      // If actor flags are present, always derive count from flags to avoid stale numeric counts
      const hasFlags = (typeof r.hotel === 'boolean') || (typeof r.guest === 'boolean') || (typeof r.byHotel === 'boolean') || (typeof r.byGuest === 'boolean');
      const count = hasFlags ? ((hotel ? 1 : 0) + (guest ? 1 : 0)) : (Number(r.count) || 0);
      const actors = normalizeActors(r);
      out[e] = { hotel, guest, count: Number(count) || 0, actors };
    }
    return out;
  }
  return {};
}

function computeCount(entry){
  if (!entry) return 0;
  if (Array.isArray(entry.actors) && entry.actors.length) return entry.actors.length;
  const hasFlags = (typeof entry.hotel === 'boolean') || (typeof entry.guest === 'boolean');
  if (hasFlags) return (entry.hotel ? 1 : 0) + (entry.guest ? 1 : 0);
  return Number(entry.count) || 0;
}

function setActor(entry, actor, value){
  const e = { ...(entry || { hotel:false, guest:false, count:0, actors:[] }) };
  const key = roleKey(actor);
  const want = !!value;
  const aid  = (actor && typeof actor === 'object' && actor.id) ? String(actor.id) : null;
  const arole = (actor && typeof actor === 'object' && actor.role) ? actor.role : (key === 'guest' ? 'guest' : 'staff');
  let actors = Array.isArray(e.actors) ? e.actors.slice() : [];
  if (aid) {
    const idx = actors.findIndex(a => String(a.id) === aid);
    if (want) { if (idx === -1) actors.push({ id: aid, role: arole }); }
    else { if (idx !== -1) actors.splice(idx, 1); }
  }
  e.actors = actors;
  e[key] = want;                         // keep legacy flags
  const hasGuest = actors.some(a => a.role === 'guest') || e.guest === true;
  const hasStaff = actors.some(a => a.role === 'staff') || e.hotel === true;
  e.guest = hasGuest;
  e.hotel = hasStaff;
  e.count = computeCount(e);
  return e;
}
function applyDeltaToReactions(existing = [], emoji, delta) {
  const em = String(emoji || '').trim();
  const d = Number(delta) || 0;
  if (!em || !d) return Array.isArray(existing) ? existing.slice() : [];
  const out = Array.isArray(existing) ? existing.map(r => ({ emoji: r.emoji, count: Number(r.count)||0 })) : [];
  const idx = out.findIndex(r => r.emoji === em);
  if (idx === -1 && d > 0) {
    out.push({ emoji: em, count: d });
    return out;
  }
  if (idx !== -1) {
    const next = (out[idx].count || 0) + d;
    if (next <= 0) {
      out.splice(idx, 1);
    } else {
      out[idx] = { emoji: em, count: next };
    }
  }
  return out;
}
// ------------------------------------------------------------------------

function getThreadKey(propertyId, userUid) {
  return `${toIdString(propertyId)}:${userUid}`;
}

function getRoomKey(propertyId, userUid) {
  return `concierge:${toIdString(propertyId)}:${userUid}`;
}

// --- seen de-dup helpers -------------------------------------------------
function makeSeenSig({ role, actorId, messageId, at }) {
  const r = role || 'staff';
  const a = actorId || 'unknown';
  const m = (messageId != null) ? String(messageId) : 'thread';
  // round to 1s to collapse repeated emits that differ by milliseconds
  const ts = at ? Math.floor(new Date(at).getTime() / 1000) : 0;
  return `${r}:${a}:${m}:${ts}`;
}
// ------------------------------------------------------------------------

// --- queued reaction retry helper (handles tight races) -----------------
function scheduleApplyQueuedReactions(threadKey, messageId, attempt = 0) {
  const MAX_TRIES = 10;       // try for ~3s total
  const DELAY_MS  = 300;
  try {
    const st = useConciergeStore.getState();
    const list = st.messagesByThread?.[threadKey] || [];
    const idx = list.findIndex(m => String(m._id || m.id || m.tempId) === String(messageId));
    if (idx !== -1) {
      // message is present now: apply and clear immediately if queued exists
      const pendingMap = st.pendingReactionsByThread?.[threadKey] || {};
      const queued = pendingMap[String(messageId)];
      if (queued) {
        const nextList = [...list];
        nextList[idx] = { ...nextList[idx], reactions: mapToArray(queued), updating: false }; 
        const nextPR = { ...(st.pendingReactionsByThread || {}) };
        const bucket = { ...(nextPR[threadKey] || {}) };
        delete bucket[String(messageId)];
        nextPR[threadKey] = bucket;
        useConciergeStore.setState({
          messagesByThread: { ...(st.messagesByThread || {}), [threadKey]: sortChronoAsc(nextList) },
          pendingReactionsByThread: nextPR,
        });
        try { console.debug('[conciergeStore] retry applied queued reactions', { threadKey, messageId: String(messageId) }); } catch {}
      }
      return; // done
    }
    if (attempt >= MAX_TRIES) return;
    setTimeout(() => scheduleApplyQueuedReactions(threadKey, messageId, attempt + 1), DELAY_MS);
  } catch (e) {
    try { console.warn('[conciergeStore] scheduleApplyQueuedReactions error', e); } catch {}
  }
}
// ------------------------------------------------------------------------

// --- queued-delete rescue: proactively flush queued deletes when server _id becomes available ---
function tryFlushQueuedDeletes(pid, userUid) {
  const key = getThreadKey(pid, userUid);
  try { console.debug('[conciergeStore][presence] _bumpPresenceLocal', { key, role, isOnline: !!isOnline, lastActive, at: new Date().toISOString() }); } catch {}
  const st = useConciergeStore.getState();
  const queued = st.pendingDeletesByThread?.[key] || {};
  const list = st.messagesByThread?.[key] || [];
  const tempIds = Object.keys(queued);
  if (!tempIds.length) return;

  for (const tId of tempIds) {
    const delivered = list.find(m => m && m.tempId === tId && (m._id || m.id));
    const deliveredId = delivered && (delivered._id || delivered.id);
    if (deliveredId) {
      const { hardDelete, deleteAttachments } = queued[tId] || {};
      // remove queue entry
      useConciergeStore.setState((state) => {
        const m = { ...(state.pendingDeletesByThread?.[key] || {}) };
        delete m[tId];
        return { pendingDeletesByThread: { ...(state.pendingDeletesByThread || {}), [key]: m } };
      });
      try { console.debug('[conciergeStore] RESCUE QUEUED DELETE', { key, tempId: tId, deliveredId: String(deliveredId) }); } catch {}
      // fire delete against the delivered server id
      useConciergeStore.getState().deleteMessage({
        propertyId: pid,
        userUid,
        messageId: String(deliveredId),
        hardDelete: !!hardDelete,
        deleteAttachments: !!deleteAttachments,
      });
    }
  }
}

export const useConciergeStore = create((set, get) => ({
  threads: [],
  messagesByThread: {},
  activeThread: null,
  loading: false,
  error: null,
  socket: null,
  connected: false,
  // presence heartbeat timer (cleared on room switch / unmount)
  __presenceTimerId: null,
  // auto-expiry timers for online flags (guest/staff) per thread
  __presenceExpireTimers: {},
  // Bump presence locally (optimistic) and schedule an expiry fallback
  _bumpPresenceLocal({ propertyId, userUid, role, isOnline = true, lastActive }) {
    const pid = toIdString(propertyId);
    if (!pid || !userUid || !role) return;
    const key = getThreadKey(pid, userUid);
    const lastActiveIso = lastActive ? new Date(lastActive).toISOString() : new Date().toISOString();
    const nowIso = new Date().toISOString();
    set((state) => ({
      
      onlineStatusByThread: {
        ...(state.onlineStatusByThread || {}),
        [key]: {
          ...(state.onlineStatusByThread?.[key] || {}),
          [role]: {
            isOnline: !!isOnline,
            lastActive: lastActiveIso,
            updatedAt: nowIso,
          },
          
        },
      },
    }));
    try { console.debug('[conciergeStore][presence] state updated', { key, role, isOnline: !!isOnline }); } catch {}
    try { get()._schedulePresenceExpiry({ key, role, updatedAt: nowIso }); } catch {}
  },
  // Schedule auto-offline unless a newer update arrives
  _schedulePresenceExpiry({ key, role, updatedAt }) {
    const timers = get().__presenceExpireTimers || {};
    const tKey = `${key}:${role}`;
    try { console.debug('[conciergeStore][presence] schedule expiry', { key, role, updatedAt, windowMs: ONLINE_WINDOW_MS }); } catch {}
    if (timers[tKey]) {
      try { clearTimeout(timers[tKey]); } catch {}
    }
    const id = setTimeout(() => {
      try { console.debug('[conciergeStore][presence] expiry timer fired', { key, role, expectedUpdatedAt: updatedAt, current: get().onlineStatusByThread?.[key]?.[role] }); } catch {}
      try {
        const st = get();
        const cur = st.onlineStatusByThread?.[key]?.[role];
        // if there's a newer update, don't flip offline
        if (!cur || (cur.updatedAt && cur.updatedAt !== updatedAt)) return;
        set((state) => ({
          onlineStatusByThread: {
            ...(state.onlineStatusByThread || {}),
            [key]: {
              ...(state.onlineStatusByThread?.[key] || {}),
              [role]: { ...(state.onlineStatusByThread?.[key]?.[role] || {}), isOnline: false },
            },
          },
        }));
      } catch {}
   }, ONLINE_WINDOW_MS);
    timers[tKey] = id;
    set({ __presenceExpireTimers: timers });
  },
  // --- Presence heartbeat helpers (staff-side) ---------------------------
  startPresenceHeartbeat(propertyId, userUid) {
    const pid = toIdString(propertyId);
    const uid = userUid;
    if (!pid || !uid) return;

    // Clear previous timer if any
    const prevId = get().__presenceTimerId;
    if (prevId) {
      try { clearInterval(prevId); } catch {}
      set({ __presenceTimerId: null });
    }

    // Kick immediately then every 30s
    try { get().emitPresenceHeartbeat(pid, uid); } catch {}
    const id = setInterval(() => {
      try { get().emitPresenceHeartbeat(pid, uid); } catch {}
     }, HEARTBEAT_MS);
    set({ __presenceTimerId: id });
  },

  stopPresenceHeartbeat() {
    const prevId = get().__presenceTimerId;
    if (prevId) {
      try { clearInterval(prevId); } catch {}
      set({ __presenceTimerId: null });
    }
  },
  // ----------------------------------------------------------------------
  onlineStatusByThread: {}, // { propertyId_userUid: { guest: { isOnline: boolean, lastActive: Date }, staff: { isOnline: boolean, lastActive: Date } } }
  // current logged-in staff user (for sender/actor stamping)
  me: null,
  setMe(user) { set({ me: user || null }); },

  // --- Quick Responses ----------------------------------------------------
  quickResponses: [],
  quickResponsesLoading: false,
  async fetchQuickResponses(propertyId) {
    try {
      const pid = toIdString(
        propertyId ||
        (typeof localStorage !== 'undefined' && (localStorage.getItem('activePropertyId') || localStorage.getItem('propertyId')))
      );
      if (!pid) return;
      set({ quickResponsesLoading: true });
      const list = await conciergeApi.getQuickResponses({ propertyId: pid });
      set({
        quickResponses: Array.isArray(list) ? list : [],
        quickResponsesLoading: false,
      });
    } catch (e) {
      set({ quickResponsesLoading: false });
    }
  },
  async saveQuickResponses(propertyId, responses) {
    try {
      const pid = toIdString(
        propertyId ||
        (typeof localStorage !== 'undefined' && (localStorage.getItem('activePropertyId') || localStorage.getItem('propertyId')))
      );
      if (!pid) return { success: false, error: 'propertyId missing' };
      const payload = Array.isArray(responses) ? responses : [];
      const res = await conciergeApi.saveQuickResponses({ propertyId: pid, responses: payload });
      if (res && res.success) {
        set({ quickResponses: payload });
      }
      return res;
    } catch (e) {
      return { success: false, error: String(e?.message || e) };
    }
  },

  // --- Automations --------------------------------------------------------
  automations: [],
  automationsLoading: false,
  async fetchAutomations(propertyId) {
    try {
      const pid = toIdString(
        propertyId ||
        (typeof localStorage !== 'undefined' && (localStorage.getItem('activePropertyId') || localStorage.getItem('propertyId')))
      );
      if (!pid) return;
      set({ automationsLoading: true });
      const rules = await conciergeApi.getAutomations({ propertyId: pid });
      set({ automations: Array.isArray(rules) ? rules : [], automationsLoading: false });
    } catch (e) {
      set({ automationsLoading: false });
    }
  },
  async saveAutomations(propertyId, rules) {
    try {
      const pid = toIdString(
        propertyId ||
        (typeof localStorage !== 'undefined' && (localStorage.getItem('activePropertyId') || localStorage.getItem('propertyId')))
      );
      if (!pid) return { success: false, error: 'propertyId missing' };
      const payload = Array.isArray(rules) ? rules : [];
      const res = await conciergeApi.saveAutomations({ propertyId: pid, rules: payload });
      if (res && res.success) set({ automations: payload });
      return res;
    } catch (e) {
      return { success: false, error: String(e?.message || e) };
    }
  },
  // -----------------------------------------------------------------------
  loadingHistoryByThread: {},
  endOfHistoryByThread: {},
  replyTargetByThread: {},  // { [threadKey]: { messageId, snapshot } }
  editingByThread: {},      // { [threadKey]: { messageId, draft, original } }

  selectedByThread: {},     // { [threadKey]: string[] }
  selectionModeByThread: {}, // { [threadKey]: boolean }
  lastAnchorByThread: {},    // { [threadKey]: string | null } // last clicked id for range select

  // queued deletes for messages that were deleted before server _id arrives
  // shape: { [threadKey]: { [tempId]: { hardDelete: boolean, deleteAttachments: boolean, at: number } } }
  pendingDeletesByThread: {},
  pendingReactionsByThread: {}, // { [threadKey]: { [messageId]: reactions[] } }
  // de-duplication caches
  lastSeenSigByThread: {},       // { [threadKey]: string }
  lastSeenSigAtMsByThread: {},   // { [threadKey]: number }
  lastMarkReadAtMsByThread: {},  // { [threadKey]: number }

  // --- Selection mode helpers ---
  isSelectionMode(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    const st = get();
    return !!(st.selectionModeByThread?.[key]);
  },
  getSelectedCount(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    return (get().selectedByThread?.[key] || []).length;
  },
  enterSelectionMode(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    console.debug('[conciergeStore] enterSelectionMode', { key });
    set(state => ({ selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: true } }));
  },
  exitSelectionMode(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    set(state => ({
      selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: false },
      selectedByThread: { ...(state.selectedByThread || {}), [key]: [] },
      lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [key]: null },
    }));
  },

  // ----- Selection helpers -----
  getSelection(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    return (get().selectedByThread?.[key] || []);
  },
  isSelected(propertyId, userUid, messageId) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    const sel = (get().selectedByThread?.[key] || []);
    return sel.includes(messageId);
  },
  clearSelection(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    set(state => ({
      selectedByThread: { ...(state.selectedByThread || {}), [key]: [] },
      selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: false },
      lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [key]: null },
    }));
  },
  selectExclusive({ propertyId, userUid, messageId }) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    console.debug('[conciergeStore] selectExclusive', { key, messageId });
    set(state => ({
      selectedByThread: { ...(state.selectedByThread || {}), [key]: messageId ? [messageId] : [] },
      selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: !!messageId },
      lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [key]: messageId || null },
    }));
  },
  toggleSelectMessage({ propertyId, userUid, messageId }) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    set(state => {
      const cur = new Set(state.selectedByThread?.[key] || []);
      if (cur.has(messageId)) cur.delete(messageId); else cur.add(messageId);
      const nextArr = Array.from(cur);
      const nextFlag = nextArr.length > 0;
      const next = {
        selectedByThread: { ...(state.selectedByThread || {}), [key]: nextArr },
        selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: nextFlag },
        lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [key]: messageId },
      };
      console.debug('[conciergeStore] toggleSelectMessage', { key, nextCount: nextArr.length, nextFlag });
      return next;
    });
  },

  selectRange({ propertyId, userUid, anchorId, targetId, inclusive = true }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const st = get();
    const list = st.messagesByThread?.[key] || [];
    const ids = list.map(m => m.id || m._id || m.tempId).filter(Boolean);
    const a = ids.indexOf(anchorId);
    const b = ids.indexOf(targetId);
    if (a === -1 || b === -1) return;
    const [lo, hi] = a < b ? [a, b] : [b, a];
    const slice = ids.slice(lo, hi + (inclusive ? 1 : 0));
    set(state => ({
      selectedByThread: { ...(state.selectedByThread || {}), [key]: slice },
      selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: slice.length > 0 },
      lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [key]: targetId },
    }));
  },

  initSocket(baseUrl) {
    const state = get();
    if (state.socket) return state.socket;

    const { url, source } = resolveSocketUrl(baseUrl);

    const socket = io(url, { transports: ['websocket'], withCredentials: true });

    socket.on('connect', () => {
      set({ connected: true });
      try {
        const { activeThread } = get();
        if (activeThread && activeThread.propertyId && activeThread.userUid) {
          const pid = toIdString(activeThread.propertyId);
          console.debug('[conciergeStore] rejoin on connect', { pid, userUid: activeThread.userUid });
          get().joinThreadRoom(pid, activeThread.userUid);
          try { get().startPresenceHeartbeat(pid, activeThread.userUid); } catch {}
        }
      } catch (e) {
        console.warn('[conciergeStore] rejoin on connect failed', e);
      }
    });
    socket.on('disconnect', () => {
      set({ connected: false });
      try { get().stopPresenceHeartbeat(); } catch {}
      try {
        const at = get().activeThread;
        if (at && at.propertyId && at.userUid) {
          get()._bumpPresenceLocal({
            propertyId: toIdString(at.propertyId),
            userUid: at.userUid,
            role: 'staff',
            isOnline: false,
            lastActive: new Date().toISOString(),
          });
        }
      } catch {}
    });

    // Handle concierge-specific status updates (guest/staff presence)
    socket.on('concierge:status_update', (data) => {
      try {
        const { propertyId, userUid, role, isOnline, lastActive } = data || {};
        if (!propertyId || !userUid || !role) return;
        get()._bumpPresenceLocal({ propertyId, userUid, role, isOnline, lastActive });
        console.debug('[conciergeStore] concierge:status_update received', { propertyId: toIdString(propertyId), userUid, role, isOnline });
        try { console.debug('[conciergeStore][presence] concierge:status_update detail', data); } catch {}
      } catch (e) {
        console.warn('[conciergeStore] concierge:status_update handler error', e);
      }
    });

    // Lightweight immediate presence push (recommended backend event)
    socket.on('presence:update', (data) => {
      try {
        const { propertyId, userUid, role, isOnline, lastActive } = data || {};
        if (!propertyId || !userUid || !role) return;
        get()._bumpPresenceLocal({ propertyId, userUid, role, isOnline, lastActive });
        console.debug('[conciergeStore] presence:update received', { propertyId: toIdString(propertyId), 
          userUid, role, isOnline });
          try { console.debug('[conciergeStore][presence] presence:update detail', data); } catch {}
      } catch (e) {
        console.warn('[conciergeStore] presence:update handler error', e);
      }
    });

    // When the window becomes visible or focused, make sure the active thread
    // is marked read so the backend receives seen/markRead without requiring
    // a click on the thread list.
    try {
      if (typeof window !== 'undefined') {
        const ensureActiveSeen = () => {
          try {
            const st = get();
            const at = st && st.activeThread;
            if (!at || !at.propertyId || !at.userUid) return;
            if (typeof document === 'undefined' || document.visibilityState === 'visible') {
              st.markRead(toIdString(at.propertyId), at.userUid);
            }
          } catch {}
        };
        window.addEventListener('visibilitychange', ensureActiveSeen);
        window.addEventListener('focus', ensureActiveSeen);
      }
    } catch {}

    // React to tab visibility (pause/resume heartbeat; send offline on hide)
    try {
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const handleVis = () => {
          const st = get();
          const at = st.activeThread;
          if (!at || !at.propertyId || !at.userUid) return;
          const pid = toIdString(at.propertyId);
          if (document.visibilityState === 'visible') {
            try { st.emitPresenceHeartbeat(pid, at.userUid); } catch {}
            try { st.startPresenceHeartbeat(pid, at.userUid); } catch {}
          } else {
            try { st.emitPresenceOffline(pid, at.userUid); } catch {}
            try { st.stopPresenceHeartbeat(); } catch {}
          }
        };
        document.addEventListener('visibilitychange', handleVis);
        window.addEventListener('beforeunload', () => {
          try {
            const st = get();
            const at = st.activeThread;
            if (!at || !at.propertyId || !at.userUid) return;
            st.emitPresenceOffline(toIdString(at.propertyId), at.userUid);
          } catch {}
        });
      }
    } catch {}

    // Real-time events from server
    socket.on('newMessage', (msg) => {
      try {
        // Only handle concierge-kind messages or those with propertyId/userUid
        if (!msg || !msg.propertyId || !msg.userUid) return;

        // --- Provisional/Optimistic message handling ---
        const pid = toIdString(msg.propertyId);
        const key = getThreadKey(pid, msg.userUid);
        if (msg.tempId && !msg._id) {
          // Optimistic provisional
          set(state => {
            const prev = state.messagesByThread?.[key] || [];
            const merged = dedupeMessages([...prev, normalizeIncoming({ ...msg, sending: true })]);
            return { messagesByThread: { ...(state.messagesByThread || {}), [key]: sortChronoAsc(merged) } };
          });
          try { console.debug('[conciergeStore] provisional message inserted', { key, tempId: msg.tempId }); } catch {}
          return;
        }
        if (msg.tempId && msg._id) {
          // Replacement path: replace optimistic with confirmed
          set(state => {
            let list = [...(state.messagesByThread?.[key] || [])];
            const idx = list.findIndex(m => m.tempId && m.tempId === msg.tempId);
            if (idx >= 0) {
              list[idx] = normalizeIncoming({ ...msg, sending: false });
            } else {
              list.push(normalizeIncoming({ ...msg, sending: false }));
            }
            return { messagesByThread: { ...(state.messagesByThread || {}), [key]: sortChronoAsc(dedupeMessages(list)) } };
          });
          try { console.debug('[conciergeStore] provisional replaced with confirmed', { key, tempId: msg.tempId, id: msg._id }); } catch {}
          return;
        }

        get().handleNewMessage(msg);
        // Optimistically mark guest online immediately when they send anything
        try {
          if (senderRoleOf(msg) === 'guest') {
            get()._bumpPresenceLocal({
              propertyId: msg.propertyId,
              userUid: msg.userUid,
              role: 'guest',
              isOnline: true,
              lastActive: msg.timestamp || msg.createdAt,
            });
          }
        } catch {}
        // Auto-mark as seen when a guest message arrives for the active thread
        try {
          const at = get().activeThread;
          if (
            at && msg && msg.propertyId && msg.userUid &&
            toIdString(at.propertyId) === toIdString(msg.propertyId) &&
            at.userUid === msg.userUid &&
            senderRoleOf(msg) === 'guest'
          ) {
            const mid = String(msg._id || msg.id || msg.tempId || '');
            if (mid && (typeof document === 'undefined' || document.visibilityState === 'visible')) {
              get().markSeen({ propertyId: toIdString(msg.propertyId), userUid: msg.userUid, messageId: mid });
            }
          }
        } catch {}
      } catch (e) {}
    });

    socket.on('messageSeen', (data) => {
      try {
        console.debug('[conciergeStore] socket messageSeen received', data);
        if (!data || !data.propertyId || !data.userUid || !data.messageId) return;
        get().handleMessageSeen(data);
      } catch (e) {
        console.warn('[conciergeStore] messageSeen handler error', e);
      }
    });

    // Unified role-aware read receipt (messages:seen)
    socket.on('messages:seen', (data) => {
      try {
        console.debug('[conciergeStore] socket messages:seen received', data);
        const { propertyId, userUid } = data || {};
        if (!propertyId || !userUid) return;
        const pid = toIdString(propertyId);
        const key = getThreadKey(pid, userUid);
        // De-dup: ignore identical signatures within a short window
        const sig = makeSeenSig({ role: data.role, actorId: data.actorId, messageId: data.messageId, at: data.at });
        const st = get();
        const lastSig = st.lastSeenSigByThread?.[key];
        const lastAtMs = st.lastSeenSigAtMsByThread?.[key] || 0;
        const nowMs = Date.now();
        if (lastSig === sig && (nowMs - lastAtMs) < 1500) {
          try { console.debug('[conciergeStore] messages:seen deduped', { key, sig }); } catch {}
          return;
        }
        // record and apply
        set((state) => ({
          lastSeenSigByThread: { ...(state.lastSeenSigByThread || {}), [key]: sig },
          lastSeenSigAtMsByThread: { ...(state.lastSeenSigAtMsByThread || {}), [key]: nowMs },
        }));
        // Soft warn if messageId is missing (thread-level seen event)
        if (data && (data.messageId == null)) {
          try { console.warn('[conciergeStore] messages:seen arrived without messageId (thread-level). Per-message seenBy will update after history loads or markSeen runs.'); } catch {}
        }
        get().applySeen(data);
      } catch (e) {
        console.warn('[conciergeStore] messages:seen handler error', e);
      }
    });
    // --- Seen aliases (backends may emit different names) ---
    const handleSeenAlias = (data) => {
      try {
        console.debug('[conciergeStore] socket seen(alias) received', data);
        const { propertyId, userUid } = data || {};
        if (!propertyId || !userUid) return;
        const pid = toIdString(propertyId);
        const key = getThreadKey(pid, userUid);
        const sig = makeSeenSig({ role: data.role, actorId: data.actorId, messageId: data.messageId, at: data.at });
        const st = get();
        const lastSig = st.lastSeenSigByThread?.[key];
        const lastAtMs = st.lastSeenSigAtMsByThread?.[key] || 0;
        const nowMs = Date.now();
        if (lastSig === sig && (nowMs - lastAtMs) < 1500) {
          try { console.debug('[conciergeStore] seen(alias) deduped', { key, sig }); } catch {}
          return;
        }
        set((state) => ({
          lastSeenSigByThread: { ...(state.lastSeenSigByThread || {}), [key]: sig },
          lastSeenSigAtMsByThread: { ...(state.lastSeenSigAtMsByThread || {}), [key]: nowMs },
        }));
        get().applySeen(data);
      } catch (e) {
        console.warn('[conciergeStore] seen(alias) handler error', e);
      }
    };
    socket.on('message:seen', handleSeenAlias);
    socket.on('concierge:messages:seen', handleSeenAlias);
    socket.on('concierge:messageSeen', handleSeenAlias);

    socket.on('messageDeleted', (data) => {
      try {
        const { propertyId, userUid, messageId, hardDelete } = data || {};
        if (!propertyId || !userUid || !messageId) return;
        get().applyMessageDeleted({ propertyId, userUid, messageId, hardDelete });
      } catch (e) {}
    });

    socket.on('messageUpdated', (data) => {
      try {
        const { propertyId, userUid, messageId, attachments, content, editedAt } = data || {};
        if (!propertyId || !userUid || !messageId) return;
        get().applyMessageUpdated({ propertyId, userUid, messageId, attachments, content, editedAt });
      } catch (e) {}
    });

    socket.on('messageReacted', (data) => {
      try {
        const { propertyId, userUid, messageId, reactions } = data || {};
        if (!propertyId || !userUid || !messageId) return;
        get().applyMessageReactions({ propertyId, userUid, messageId, payload: data });
      } catch (e) {}
    });

    // Some backends emit namespaced concierge events; handle both
    socket.on('concierge:messageReacted', (data) => {
      try {
        const { propertyId, userUid, messageId, reactions } = data || {};
        if (!propertyId || !userUid || !messageId) return;
        get().applyMessageReactions({ propertyId, userUid, messageId, payload: data });
      } catch (e) {}
    });

    // Alias event names for reactions
    const handleReact = (data) => {
      try {
        const { propertyId, userUid, messageId, reactions } = data || {};
        if (!propertyId || !userUid || !messageId) return;
        get().applyMessageReactions({ propertyId, userUid, messageId, payload: data });
      } catch (e) {}
    };
    socket.on('reactionUpdated', handleReact);
    socket.on('messageReaction', handleReact);
    socket.on('message:reacted', handleReact);
    socket.on('reaction:updated', handleReact);
    socket.on('concierge:reactionUpdated', handleReact);

    // ---- Thread-level updates (keep thread list in sync instantly) ----
    const handleThreadUpdated = (payload) => {
      try {
        if (!payload) return;
        const pid = toIdString(payload.propertyId);
        const userUid = payload.userUid;
        if (!pid || !userUid) return;
        const idLike = String(payload._id || payload.id || '');

        const unreadUpdate = (payload.unread != null)
          ? (typeof payload.unread === 'number' ? { staff: payload.unread } : payload.unread)
          : undefined;

        set((state) => {
          const nextThreads = Array.isArray(state.threads)
            ? state.threads.map((t) => {
                const match =
                  (samePropId(t.propertyId, pid) && t.userUid === userUid) ||
                  (idLike && (String(t._id || t.id || '') === idLike));
                if (!match) return t;
                const out = { ...t };
                if (payload.lastMessage !== undefined) out.lastMessage = payload.lastMessage;
                if (payload.lastAt !== undefined) out.lastAt = payload.lastAt;
                if (unreadUpdate !== undefined) out.unread = { ...(out.unread || {}), ...unreadUpdate };
                if (payload.presence === undefined && out.presence) out.presence = out.presence; // preserve existing presence when update omits it
                return out;
              })
            : state.threads;
          return { threads: nextThreads };
        });
      } catch (e) {
        try { console.warn('[conciergeStore] thread:updated handler error', e); } catch {}
      }
    };
    socket.on('thread:updated', handleThreadUpdated);
    socket.on('concierge:threadUpdated', handleThreadUpdated);

    set({ socket });
    return socket;
  },

  joinThreadRoom(propertyId, userUid) {
    console.debug('[conciergeStore] joinThreadRoom', propertyId, userUid);
    const { socket } = get();
    if (!socket || !propertyId || !userUid) return;
    const payload = { kind: 'concierge', propertyId: toIdString(propertyId), userUid };
    console.debug('[conciergeStore] emit join', payload);
    try { console.debug('[conciergeStore][presence] join room -> start heartbeat', { payload }); } catch {}
    socket.emit('join', payload);
  },

  emitPresenceHeartbeat(propertyId, userUid) {
    const pid = toIdString(propertyId);
    if (!pid || !userUid) return;
    const { socket } = get();
    if (!socket) return;
    const actor = resolveCurrentActor(get);
    if (!actor || !actor.role) return;

    try {
      socket.emit('concierge:active', { propertyId: pid, userUid, role: actor.role });
      try { console.debug('[conciergeStore][presence] emit concierge:active', { propertyId: pid, userUid, role: actor.role, at: new Date().toISOString() }); } catch {}
      get()._bumpPresenceLocal({ propertyId: pid, userUid, role: actor.role, isOnline: true, lastActive: new Date().toISOString() });
    } catch (err) {
      console.warn('[conciergeStore] emitPresenceHeartbeat failed', err);
    }
  },

  emitPresenceOffline(propertyId, userUid) {
  const pid = toIdString(propertyId);
  if (!pid || !userUid) return;
  const { socket } = get();
  const actor = resolveCurrentActor(get);

  try {
    console.debug('[conciergeStore][presence] emit concierge:inactive', {
      propertyId: pid,
      userUid,
      role: actor?.role || 'staff',
      at: new Date().toISOString(),
    });
  } catch {}

  // Tell backend we went inactive (if event is supported)
  try { if (socket) socket.emit('concierge:inactive', { propertyId: pid, userUid, role: actor?.role || 'staff' }); } catch {}

  // Optimistic local flip so UI responds instantly
  try {
    get()._bumpPresenceLocal({
      propertyId: pid,
      userUid,
      role: actor?.role || 'staff',
      isOnline: false,
      lastActive: new Date().toISOString(),
    });
  } catch {}

  try { console.debug('[conciergeStore][presence] optimistic offline applied', { propertyId: pid, userUid }); } catch {}
},

  leaveThreadRoom(propertyId, userUid) {
    console.debug('[conciergeStore] leaveThreadRoom', propertyId, userUid);
    const { socket } = get();
    if (!socket || !propertyId || !userUid) return;
    try {
      const payload = { kind: 'concierge', propertyId: toIdString(propertyId), userUid };
      try { get().emitPresenceOffline(propertyId, userUid); } catch {}
      socket.emit('leave', payload);
      try { console.debug('[conciergeStore][presence] left room', { payload }); } catch {}
    } catch (e) {
      console.warn('[conciergeStore] leave emit failed (safe to ignore)', e);
    }
    try { get().stopPresenceHeartbeat(); } catch {}
  },

  setReplyTarget({ propertyId, userUid, messageId, snapshot }) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    console.debug('[conciergeStore] setReplyTarget', { key, messageId, snapshot });
    set(state => ({
      replyTargetByThread: { ...(state.replyTargetByThread || {}), [key]: messageId ? { messageId, snapshot } : null }
    }));
  },
  clearReplyTarget(propertyId, userUid) {
    const key = getThreadKey(toIdString(propertyId), userUid);
    console.debug('[conciergeStore] clearReplyTarget', { key });
    set(state => ({
      replyTargetByThread: { ...(state.replyTargetByThread || {}), [key]: null }
    }));
  },

  // ------------ Edit message (open -> draft -> submit/cancel) ------------
  startEditMessage({ propertyId, userUid, messageId }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const list = (get().messagesByThread?.[key] || []);
    const cur = list.find(m => (m._id || m.id) === messageId);
    const draft = (cur?.text ?? cur?.content ?? '').toString();
    set(state => ({
      editingByThread: { ...(state.editingByThread || {}), [key]: { messageId, draft, original: cur || null } }
    }));
  },
  setEditDraft({ propertyId, userUid, text }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set(state => {
      const cur = state.editingByThread?.[key];
      if (!cur) return {};
      return { editingByThread: { ...state.editingByThread, [key]: { ...cur, draft: String(text ?? '') } } };
    });
  },
  cancelEditMessage({ propertyId, userUid }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set(state => {
      const next = { ...(state.editingByThread || {}) };
      delete next[key];
      return { editingByThread: next };
    });
  },
  async submitEditMessage({ propertyId, userUid }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const st = get();
    const edit = st.editingByThread?.[key];
    if (!edit?.messageId) return;
    const draft = (edit.draft ?? '').toString();

    // optimistic UI
    set(state => {
      let list = [...(state.messagesByThread?.[key] || [])];
      list = list.map(m => ((m._id || m.id) === edit.messageId)
        ? { ...m, content: draft, text: draft, updating: true, editedAt: new Date().toISOString() }
        : m);
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: sortChronoAsc(list) } };
    });

    try {
      const res = await conciergeApi.editConciergeMessage({ propertyId: pid, userUid, messageId: edit.messageId, content: draft });
      const updated = res?.message || res || {};
      get().applyMessageUpdated({
        propertyId: pid,
        userUid,
        messageId: edit.messageId,
        content: draft,
        attachments: updated.attachments,
        editedAt: updated.editedAt || new Date().toISOString(),
      });
    } finally {
      set(state => {
        const next = { ...(state.editingByThread || {}) };
        delete next[key];
        return { editingByThread: next };
      });
    }
  },

  async fetchThreads(propertyId, opts) {
    set({ loading: true, error: null });
    try {
      const pid = propertyId || (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId'));
      console.debug('[conciergeStore] fetchThreads propertyId=', pid);
      if (!pid) {
        set({ loading: false });
        return;
      }
      const raw = await conciergeApi.getThreads({ propertyId: pid, ...(opts || {}) });
      const threads = Array.isArray(raw) ? raw.map(normalizeThread) : [];
      set({ threads, loading: false });
    } catch (err) {
      set({ error: err?.message || 'Failed to fetch threads', loading: false });
    }
  },

  async fetchHistory(propertyId, userUid, opts) {
    set({ loading: true, error: null });
    try {
      const pid = toIdString(propertyId || (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId')));
      console.debug('[conciergeStore] fetchHistory pid=', pid, 'userUid=', userUid);
      if (!pid || !userUid) {
        set({ loading: false });
        return;
      }
      const key = getThreadKey(pid, userUid);
      const st = get();
      // prevent overlapping loads and stop when end reached
      if (st.endOfHistoryByThread[key]) {
        console.debug('[conciergeStore] fetchHistory aborted: end reached for', key);
        set({ loading: false });
        return;
      }
      if (st.loadingHistoryByThread[key]) {
        console.debug('[conciergeStore] fetchHistory aborted: already loading for', key);
        set({ loading: false });
        return;
      }
      set(state => ({ loadingHistoryByThread: { ...state.loadingHistoryByThread, [key]: true } }));
      const messages = await conciergeApi.getHistory({ propertyId: pid, userUid, ...(opts || {}) });
      console.debug('[conciergeStore] fetchHistory raw len=', Array.isArray(messages) ? messages.length : '(not array)');
      const incoming = (messages || []).map(normalizeIncoming);
      set(state => {
      // Ensure backend gets a read/seen update for the active thread once
      // history is available, without requiring a click on the thread list.
      try {
        const at = get().activeThread;
        if (at && toIdString(at.propertyId) === pid && at.userUid === userUid) {
          queueMicrotask(() => { try { get().markRead(pid, userUid); } catch {} });
        }
      } catch {}
        const prev = state.messagesByThread[key] || [];
        console.debug('[conciergeStore] fetchHistory prev len=', prev.length, 'incoming len=', incoming.length, 'sample incoming=', pickSample(incoming, 2).map(fmtMsg));

        // --- Reconciliation logic to replace optimistic messages when real ones arrive ---
        let merged = [...(prev || [])];

        for (const deliveredRaw of incoming) {
          const delivered = deliveredRaw;
          const deliveredText = delivered.text ?? delivered.content ?? '';

          const idx = merged.findIndex((m) => {
            const byTemp =
              m.tempId && delivered.tempId && String(m.tempId) === String(delivered.tempId);
            const byContent =
              !delivered.tempId &&
              m.sending === true &&
              sameSender(m.sender, delivered.sender) &&
              ((m.text ?? m.content ?? '') === deliveredText);
            return byTemp || byContent;
          });

          if (idx >= 0) {
            merged[idx] = { ...delivered, sending: false, error: false };
          } else {
            merged.push({ ...delivered, sending: false, error: false });
          }
        }

        merged = dedupeMessages(merged);
        merged = sortChronoAsc(merged);
        console.debug('[conciergeStore] fetchHistory merged len=', merged.length, 'sample merged last=', pickSample(merged.slice(-2), 2).map(fmtMsg));

        // Determine end-of-history: zero results OR (paged and fewer than limit)
        const lim = (opts && opts.limit) ? Number(opts.limit) : undefined;
        const isEnd = (incoming.length === 0) || (lim ? incoming.length < lim : false);

        return {
          messagesByThread: { ...state.messagesByThread, [key]: merged },
          loading: false,
          loadingHistoryByThread: { ...state.loadingHistoryByThread, [key]: false },
          endOfHistoryByThread: { ...state.endOfHistoryByThread, [key]: isEnd },
        };
      });
      // --- RESCUE ON HISTORY: after merging, flush any queued deletes that now have delivered ids ---
      try { queueMicrotask(() => { try { tryFlushQueuedDeletes(pid, userUid); } catch (e) { console.warn('[conciergeStore] fetchHistory rescue flush failed', e); } }); } catch {}
    } catch (err) {
      set({ error: err?.message || 'Failed to fetch history', loading: false });
      try {
        const pid2 = toIdString(propertyId || (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId')));
        const key2 = getThreadKey(pid2, userUid);
        set(state => ({ loadingHistoryByThread: { ...state.loadingHistoryByThread, [key2]: false } }));
      } catch {}
    }
  },

  setActiveThread(thread) {
    console.debug('[conciergeStore] setActiveThread', thread);
    const prev = get().activeThread;

    // 🔧 normalize to ensure stable propertyId and ids
    const normalized = normalizeThread(thread);
    set({ activeThread: normalized });

    // Initialize per-thread buckets so UI can render immediately
    if (normalized && normalized.propertyId && normalized.userUid) {
      const pid = toIdString(normalized.propertyId);
      const uid = normalized.userUid;
      const k = getThreadKey(pid, uid);
      set(state => ({
        endOfHistoryByThread: { ...state.endOfHistoryByThread, [k]: false },
        messagesByThread: { ...state.messagesByThread, [k]: state.messagesByThread[k] || [] },
        selectedByThread: { ...(state.selectedByThread || {}), [k]: [] },
        selectionModeByThread: { ...(state.selectionModeByThread || {}), [k]: false },
        lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [k]: null },
      }));
    }

    try {
      console.debug('[conciergeStore] activeThread set to', normalized && { propertyId: normalized.propertyId, userUid: normalized.userUid });
    } catch {}

    // Ensure socket exists
    const propertyId = (normalized && normalized.propertyId) || (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId'));
    get().initSocket();

    // Leave previous room
    if (prev && prev.propertyId && prev.userUid) {
      get().leaveThreadRoom(prev.propertyId, prev.userUid);
    }
    // Stop any existing heartbeat
    try { get().stopPresenceHeartbeat(); } catch {}

    // Join new room, mark read, and fetch history immediately
    if (normalized && normalized.propertyId && normalized.userUid) {
      const pid = toIdString(normalized.propertyId);
      const uid = normalized.userUid;
      const actor = resolveCurrentActor(get); // Get current actor (staff)
      get().joinThreadRoom(pid, uid);
      // Emit concierge:join to inform backend of active status
      if (get().socket && actor && actor.role) {
        get().socket.emit('concierge:join', { propertyId: pid, userUid: uid, role: actor.role });
      }
      try { get().emitPresenceHeartbeat(pid, uid); } catch {}
      try { get().startPresenceHeartbeat(pid, uid); } catch {}
      // Best-effort mark read on switch
      get().markRead(pid, uid);
      // 🔧 fetch immediately so the list fills without waiting for a live socket hit
      console.debug('[conciergeStore] fetchHistory for key=', getThreadKey(pid, uid));
      get().fetchHistory(pid, uid, { page: 1, limit: 50 });
      try { setTimeout(() => { try { get().markRead(pid, uid); } catch {} }, 300); } catch {}
    }
  },

  // --- Optimistic helpers for attachments-first flow ----------------------
  applyMessageDeleted({ propertyId, userUid, messageId, hardDelete = false }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      if (hardDelete) {
        list = list.filter(m => (m._id || m.id) !== messageId);
      } else {
        list = list.map(m => ((m._id || m.id) === messageId
          ? { ...m, content: '(deleted)', text: '(deleted)', attachments: [], deleted: true, deleting: false }
          : m));
      }
      list = sortChronoAsc(list);
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });
  },

  // Update a single message's content/attachments/editedAt
  applyMessageUpdated({ propertyId, userUid, messageId, attachments, content, editedAt }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      list = list.map((m) => {
        if ((m._id || m.id) !== messageId) return m;
        const next = { ...m, updating: false };
        if (attachments) next.attachments = attachments;
        if (typeof content === 'string') {
          next.content = content;
          // keep `text` mirror in sync for UI components that read text
          next.text = content;
        }
        if (editedAt) next.editedAt = editedAt;
        return next;
      });
      list = sortChronoAsc(list);
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });
  },

  // Unified role-aware read receipt handler (thread/message level)
  applySeen({ propertyId, userUid, messageId, role = 'staff', at, actorId }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const seenAtIso = at ? new Date(at).toISOString() : new Date().toISOString();
    // Prefer provided actorId; otherwise resolve current staff id for optimistic seenBy stamping
    let resolvedActorId = actorId;
    if (!resolvedActorId && role === 'staff') {
      try {
        const actor = resolveCurrentActor(() => useConciergeStore.getState());
        if (actor && actor.id) resolvedActorId = String(actor.id);
      } catch {}
    }
    // Debug: entrypoint
    try {
      console.debug('[conciergeStore] applySeen:start', {
        key: getThreadKey(toIdString(propertyId), userUid),
        propertyId: toIdString(propertyId),
        userUid,
        role,
        messageId: messageId != null ? String(messageId) : null,
        at: seenAtIso
      });
    } catch {}

    set((state) => {
      // 1) Update messages up to target with seenBy[role], and keep legacy .seen for opposite-direction messages
      let list = [...(state.messagesByThread?.[key] || [])];
      let idx = -1;
      if (messageId != null) {
        idx = list.findIndex((m) => String(m._id || m.id || m.tempId) === String(messageId));
      } else if (list.length) {
        // Thread-level mark: cutoff should be the latest message authored by the *other* party
        if (role === 'staff') {
          idx = lastGuestMessageIndex(list);
        } else if (role === 'guest') {
          // mirror helper for staff messages
          for (let i = list.length - 1; i >= 0; i--) {
            const mm = list[i];
            if (senderRoleOf(mm) === 'staff') { idx = i; break; }
          }
          if (idx === -1) idx = list.length - 1; // fallback
        } else {
          idx = list.length - 1; // unknown role: be permissive
        }
      }

      let updatedCount = 0;

      if (idx >= 0) {
        for (let i = 0; i <= idx; i++) {
          const m = list[i] || {};
          const senderRole = senderRoleOf(m);

          // Only mark messages from the *other* party
          if (senderRole === role) continue;

          const sb = Array.isArray(m.seenBy) ? m.seenBy.slice() : [];
          const has = sb.some((s) => s && s.role === role);
          if (!has) {
            const entry = { role, at: seenAtIso };
            if (role === 'staff' && resolvedActorId) entry.id = String(resolvedActorId);
            sb.push(entry);
          }

          // Legacy boolean for current UI
          let legacySeen = m.seen;
          if (role === 'staff' && senderRole === 'guest') legacySeen = true;
          if (role === 'guest' && senderRole === 'staff') legacySeen = true;

          const nextMsg = { ...m, seenBy: sb, seen: !!legacySeen };
          if (!has || (!!legacySeen && !m.seen)) updatedCount++;
          list[i] = nextMsg;
        }
      }

      // 2) Update thread.lastSeen[role] inside threads array if present
      const nextThreads = Array.isArray(state.threads)
        ? state.threads.map((t) => {
            if (samePropId(t.propertyId, pid) && t.userUid === userUid) {
              const prevLS = t.lastSeen || {};
              const prevUnread = t.unread || {};
              const unread = (role === 'staff') ? { ...prevUnread, staff: 0 } : prevUnread;
              return { ...t, lastSeen: { ...prevLS, [role]: seenAtIso }, unread };
            }
            return t;
          })
        : state.threads;

      try {
        const lastTargetId = (idx >= 0 && list[idx]) ? String(list[idx]._id || list[idx].id || list[idx].tempId || '') : null;
        console.debug('[conciergeStore] applySeen:done', {
          key,
          role,
          updatedCount,
          uptoIndex: idx,
          lastTargetId,
          lastSeenAt: seenAtIso
        });
      } catch {}

      return {
        messagesByThread: { ...(state.messagesByThread || {}), [key]: sortChronoAsc(list) },
        threads: nextThreads
      };
    });
  },

  // Shim for legacy socket event to use unified logic
  handleMessageSeen(data) {
    // Legacy payload shape: { propertyId, userUid, messageId, seen: true, timestamp }
    const { propertyId, userUid, messageId, timestamp } = data || {};
    if (!propertyId || !userUid || !messageId) return;
    // Assume staff marked guest messages as seen (backoffice flow); role can be adjusted if needed.
    this.applySeen({ propertyId, userUid, messageId, role: 'staff', at: timestamp });
  },

  // Explicit mark-seen API with optimistic thread/message update
  async markSeen({ propertyId, userUid, messageId, at }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const iso = at ? new Date(at).toISOString() : new Date().toISOString();
    const actorObj = resolveCurrentActor(get);

    try {
      console.debug('[conciergeStore] markSeen:send', { key, propertyId: pid, userUid, messageId: messageId ? String(messageId) : null, at: iso });
    } catch {}

    // Optimistic apply
    try { this.applySeen({ propertyId: pid, userUid, messageId, role: 'staff', at: iso, actorId: actorObj && actorObj.id }); } catch {}

    // Best-effort server call (non-blocking)
    try {
      await conciergeApi.markSeen({ propertyId: pid, userUid, messageId, at: iso, actor: actorObj });
      try {
        console.debug('[conciergeStore] markSeen:ok', { key, propertyId: pid, userUid, messageId: messageId ? String(messageId) : null });
      } catch {}
    } catch (e) {
      // Do not rollback; seen is idempotent and eventual consistency is OK
      try { console.warn('[conciergeStore] markSeen API failed (non-fatal)', e); } catch {}
      try {
        console.warn('[conciergeStore] markSeen:error', { key, propertyId: pid, userUid, messageId: messageId ? String(messageId) : null, error: String(e?.message || e) });
      } catch {}
    }
  },

  // Mark the whole thread as read (optimistic for staff) and call API (idempotent).
  async markRead(propertyId, userUid) {
    const pid = toIdString(propertyId);
    if (!pid || !userUid) return;
    const key = getThreadKey(pid, userUid);
    const actorObj = resolveCurrentActor(get);

    // Cooldown to avoid duplicate markRead storms when switching threads/rerenders
    const nowMs = Date.now();
    const lastAt = (get().lastMarkReadAtMsByThread || {})[key] || 0;
    if (nowMs - lastAt < 1000) {
      try { console.debug('[conciergeStore] markRead skipped due to cooldown', { key, sinceMs: nowMs - lastAt }); } catch {}
      return;
    }
    set((state) => ({
      lastMarkReadAtMsByThread: { ...(state.lastMarkReadAtMsByThread || {}), [key]: nowMs },
    }));

    // Optimistically mark the whole thread as seen for staff on the client.
    // NOTE: applySeen with no messageId marks up to the latest message.
    try {
      this.applySeen({ propertyId: pid, userUid, role: 'staff' });
    } catch (e) {
      try { console.warn('[conciergeStore] markRead optimistic applySeen failed', e); } catch {}
    }

    // Additionally mark the latest guest message by id so the backend updates seenBy[]
    try {
      const stNow = get();
      const listNow = stNow.messagesByThread?.[key] || [];
      // find the index of the last message authored by the guest
      let lastGuestIdx = -1;
      for (let i = listNow.length - 1; i >= 0; i--) {
        if (senderRoleOf(listNow[i]) === 'guest') { lastGuestIdx = i; break; }
      }
      if (lastGuestIdx !== -1) {
        const target = listNow[lastGuestIdx];
        const mid = String(target._id || target.id || target.tempId || '');
        if (mid) {
          // Use message-level markSeen so server writes seenBy and legacy seen flags
          await get().markSeen({ propertyId: pid, userUid, messageId: mid });
        }
      }
      else {
        // History might not be loaded yet (setActiveThread calls markRead before fetchHistory).
        // Schedule a short retry to stamp the latest guest message once messages arrive.
        setTimeout(() => {
          try {
            const stLater = get();
            const l2 = stLater.messagesByThread?.[key] || [];
            let gi = -1;
            for (let j = l2.length - 1; j >= 0; j--) {
              if (senderRoleOf(l2[j]) === 'guest') { gi = j; break; }
            }
            if (gi !== -1) {
              const tgt = l2[gi];
              const mid2 = String(tgt._id || tgt.id || tgt.tempId || '');
              if (mid2) {
                stLater.markSeen({ propertyId: pid, userUid, messageId: mid2, at: new Date().toISOString() });
                try { console.debug('[conciergeStore] markRead delayed markSeen', { key, messageId: mid2 }); } catch {}
              }
            } else {
              try { console.warn('[conciergeStore] markRead retry found no guest message yet; relying on autoMarkSeenLatest'); } catch {}
            }
          } catch (e2) { try { console.warn('[conciergeStore] markRead delayed markSeen failed (non-fatal)', e2); } catch {} }
        }, 400);
      }
    } catch (e) {
      try { console.warn('[conciergeStore] markRead fallback markSeen failed (non-fatal)', e); } catch {}
    }

    // Best‑effort server call to thread‑level mark‑read (idempotent).
    try {
      await conciergeApi.markRead({ propertyId: pid, userUid, actor: actorObj });
      try { console.debug('[conciergeStore] markRead:ok', { key }); } catch {}
    } catch (e) {
      // Non‑fatal: the UI will remain optimistic and a later socket `messages:seen` will reconcile.
      try { console.warn('[conciergeStore] markRead API failed (non‑fatal)', e); } catch {}
    }
  },

  // Throttled auto-mark for IntersectionObserver callbacks (called by UI)
  autoMarkSeenLatest(propertyId, userUid) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const st = get();
    const list = st.messagesByThread?.[key] || [];
    const idx = lastGuestMessageIndex(list);
    if (idx < 0) return;
    const m = list[idx];
    if (staffHasSeen(m)) return; // already seen
    const mid = m._id || m.id || m.tempId;
    // light throttle to avoid spamming on rapid visibility toggles
    if (!st.__autoSeenThrottle) st.__autoSeenThrottle = {};
    const bucket = st.__autoSeenThrottle;
    const lastAt = bucket[key] || 0;
    const now = Date.now();
    if (now - lastAt < 800) return; // 800ms throttle
    bucket[key] = now;
    set({ __autoSeenThrottle: bucket });
    // fire
    this.markSeen({ propertyId: pid, userUid, messageId: mid });
  },

  // --- reactions helpers ---------------------------------------------------
  applyMessageReactions({ propertyId, userUid, messageId, payload, reactions, emoji, delta }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      const idx = list.findIndex((m) => String(m._id || m.id || m.tempId) === String(messageId));

      // Prefer newest backend payload fields
      const p = payload || {};
      const hasCounts = p && p.counts && (typeof p.counts === 'object');
      const hasActorAction = p && p.actor && p.action;
      const hasSnapshot = !!reactions; // legacy path from older emitters
      const hasDelta = (!hasCounts && !hasActorAction && !hasSnapshot && emoji && (Number(delta) || 0));

      // 0) Highest priority: if server sends a full reactions snapshot array, trust it entirely
      if (idx !== -1 && Array.isArray(p.reactions)) {
        const snapMap = normalizeReactionsMap(p.reactions);
        // Clean out any empty entries and derive counts
        for (const k of Object.keys(snapMap)) {
          const v = snapMap[k];
          if (!v?.hotel && !v?.guest && (Number(v?.count) || 0) <= 0 && (!Array.isArray(v?.actors) || v.actors.length === 0)) {
            delete snapMap[k];
          } else {
            // ensure counts reflect actors if present
            v.count = computeCount(v);
          }
        }
        list[idx] = { ...list[idx], reactions: mapToArray(snapMap), updating: false };
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      }

      // If message not in list yet, queue under pendingReactionsByThread using a canonical map
      if (idx === -1) {
        const pendingBucket = { ...(state.pendingReactionsByThread?.[key] || {}) };
        const prevQueued = pendingBucket[String(messageId)] || {};
        let nextQueued = normalizeReactionsMap(prevQueued);

        if (hasCounts && p.emoji) {
          const entry = setActor(nextQueued[p.emoji], 'hotel', (p.counts.hotel || 0) > 0);
          nextQueued[p.emoji] = { ...entry, guest: (p.counts.guest || 0) > 0, count: Number(p.counts.hotel||0) + Number(p.counts.guest||0) };
        } else if (hasActorAction && p.emoji) {
          const act = p.action === 'remove' ? false : true;
          nextQueued[p.emoji] = setActor(nextQueued[p.emoji], p.actor, act);
        } else if (hasSnapshot) {
          // reactions snapshot array -> preserve booleans when present and merge deterministically
          const arr = Array.isArray(reactions) ? reactions : [];
          for (const r of arr) {
            const e = r.emoji;
            if (!e) continue;
            const prev = nextQueued[e] || { hotel:false, guest:false, count:0 };
            const mergedHotel = !!(prev.hotel || r.hotel);
            const mergedGuest = !!(prev.guest || r.guest);
            const mergedCount = (typeof r.count === 'number' && !Number.isNaN(r.count))
              ? Number(r.count)
              : ((mergedHotel ? 1 : 0) + (mergedGuest ? 1 : 0));
            nextQueued[e] = { hotel: mergedHotel, guest: mergedGuest, count: mergedCount };
          }
        } else if (hasDelta) {
          // Minimal legacy support: store as count-only
          const e = String(emoji);
          const prev = nextQueued[e] || { hotel:false, guest:false, count:0 };
          nextQueued[e] = { ...prev, count: Math.max(0, (prev.count||0) + Number(delta)) };
        } else {
          return {};
        }

        const out = { ...(state.pendingReactionsByThread || {}) };
        out[key] = { ...(out[key] || {}), [String(messageId)]: nextQueued };
        try { console.log('[conciergeStore] queued reactions (actor-aware, message not found yet)', { key, messageId: String(messageId), nextQueued }); } catch {}
        try { queueMicrotask(() => scheduleApplyQueuedReactions(key, String(messageId))); } catch { setTimeout(() => scheduleApplyQueuedReactions(key, String(messageId)), 0); }
        return { pendingReactionsByThread: out };
      }

      // Message exists — merge into its reactions map
      const currentMap = normalizeReactionsMap(list[idx].reactions);
      // Cleanup: drop any entries that have no actors and zero count
      for (const k of Object.keys(currentMap)) {
        const v = currentMap[k];
        if (!v?.hotel && !v?.guest && (Number(v?.count) || 0) <= 0) delete currentMap[k];
      }
      let nextMap = { ...currentMap };

      if (hasCounts && p.emoji) {
        const prevEntry = nextMap[p.emoji] || { hotel:false, guest:false, count:0, actors:[] };
        const updated = {
          ...prevEntry,
          hotel: (p.counts.hotel || 0) > 0,
          guest: (p.counts.guest || 0) > 0,
        };
        updated.count = computeCount(updated);

        const prevArr = list[idx].reactions || [];
        const nextArr = upsertReactionArray(prevArr, p.emoji, updated);
        list[idx] = { ...list[idx], reactions: nextArr, updating: false };
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      } else if (hasActorAction && p.emoji) {
        // Highest-priority path: explicit actor/action from server.
        // Toggle only this emoji entry and update the array in-place to avoid flicker.
        const act = p.action === 'remove' ? false : true;
        const updated = setActor(nextMap[p.emoji], p.actor, act);

        const prevArr = list[idx].reactions || [];
        const nextArr = upsertReactionArray(prevArr, p.emoji, updated);
        list[idx] = { ...list[idx], reactions: nextArr, updating: false };
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      } else if (hasSnapshot) {
        // Replace with the server-provided snapshot to avoid UI flicker from partial merges
        const snap = normalizeReactionsMap(reactions);
        // prune empties & ensure counts derived from actors/flags
        for (const k of Object.keys(snap)) {
          const v = snap[k];
          if (!v?.hotel && !v?.guest && (Number(v?.count) || 0) <= 0 && (!Array.isArray(v?.actors) || v.actors.length === 0)) {
            delete snap[k];
          } else {
            v.count = computeCount(v);
          }
        }
        nextMap = snap;
      } else if (hasDelta) {
        const e = String(emoji);
        const prev = nextMap[e] || { hotel:false, guest:false, count:0 };
        nextMap[e] = { ...prev, count: Math.max(0, (prev.count||0) + Number(delta)) };
      } else {
        return {};
      }

      list[idx] = { ...list[idx], reactions: mapToArray(nextMap), updating: false };
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });
  },

  async reactToMessage({ propertyId, userUid, messageId, emoji, action }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    if (!pid || !userUid || !messageId || !emoji) {
      console.warn('[conciergeStore] reactToMessage aborted: missing args', { pid, userUid, messageId, emoji });
      return;
    }

    // Determine desired action if not given (toggle for CURRENT_ACTOR)
    const state0 = get();
    const list0 = state0.messagesByThread?.[key] || [];
    const idx0 = list0.findIndex((m) => String(m._id || m.id || m.tempId) === String(messageId));
    const curMap = normalizeReactionsMap(idx0 !== -1 ? list0[idx0].reactions : {});
    const myKey = roleKey(CURRENT_ACTOR);
    const mine = !!(curMap[emoji]?.[myKey]);
    const desired = action || (mine ? 'remove' : 'add');

    // Optimistic update
    let rollback = null;
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      const i = list.findIndex((m) => String(m._id || m.id || m.tempId) === String(messageId));
      if (i !== -1) {
        const before = normalizeReactionsMap(list[i].reactions);
        rollback = before;

        const optimisticActor = resolveCurrentActor(get);
        const updated = setActor(before[emoji], optimisticActor, desired !== 'remove');

        const nextArr = upsertReactionArray(list[i].reactions, emoji, updated);
        list[i] = { ...list[i], reactions: nextArr, updating: true };
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      }
      return {};
    });

    // Always resolve actor with helper for stamping
    const actorObj = resolveCurrentActor(get);

    // ---- DEBUG LOGGING: before API call ----
    console.debug('[conciergeStore] reactToMessage sending', {
      propertyId: pid,
      userUid,
      messageId,
      emoji,
      actor: actorObj,
      action: desired
    });

    try {
      await conciergeApi.reactToMessage({ propertyId: pid, userUid, messageId, emoji, actor: actorObj, action: desired });
      // ---- DEBUG LOGGING: after success ----
      console.debug('[conciergeStore] reactToMessage success', {
        propertyId: pid,
        userUid,
        messageId,
        emoji,
        actor: actorObj,
        action: desired
      });
      // Success: server will emit authoritative messageReacted; clear updating quickly
      set((state) => {
        let list = [...(state.messagesByThread?.[key] || [])];
        const i = list.findIndex((m) => String(m._id || m.id || m.tempId) === String(messageId));
        if (i !== -1) list[i] = { ...list[i], updating: false };
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      });
    } catch (e) {
      // ---- DEBUG LOGGING: error ----
      console.error('[conciergeStore] reactToMessage API error', e, {
        propertyId: pid,
        userUid,
        messageId,
        emoji,
        actor: actorObj,
        action: desired
      });
      console.warn('[conciergeStore] reactToMessage failed; rolling back', e);
      set((state) => {
        let list = [...(state.messagesByThread?.[key] || [])];
        const i = list.findIndex((m) => String(m._id || m.id || m.tempId) === String(messageId));
        if (i !== -1 && rollback) list[i] = { ...list[i], reactions: rollback, updating: false, error: true };
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      });
    }
  },

  async deleteMessage({ propertyId, userUid, messageId, hardDelete = false, deleteAttachments = true }) {
    // Resolve required identifiers from activeThread/localStorage if not explicitly provided
    const st = get();
    const pidResolved =
      propertyId ||
      (st.activeThread && st.activeThread.propertyId) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId'));
    const uidResolved = userUid || (st.activeThread && st.activeThread.userUid);

    if (!pidResolved || !uidResolved || !messageId) {
      console.error('[conciergeStore] deleteMessage aborted: missing pid/userUid/messageId', {
        pidResolved,
        uidResolved,
        messageId,
      });
      return;
    }

    const pid = toIdString(pidResolved);
    const key = getThreadKey(pid, uidResolved);

    // Resolve the server id we should send to API. Prefer the server `_id` if we can locate the message in state.
    const list0 = (get().messagesByThread?.[key] || []);
    const found = list0.find(m => String(m._id || m.id || m.tempId) === String(messageId));
    const apiId = found && found._id ? String(found._id) : String(messageId);
    const altId = found ? (apiId === String(found._id) ? (found.id ? String(found.id) : null) : (found._id ? String(found._id) : null)) : null;

    // --- DEBUG LOGGING ---
    try {
      console.debug('[conciergeStore] deleteMessage info', {
        threadKey: key,
        incomingMessageId: String(messageId),
        apiId,
        altId,
        foundExists: !!found,
        foundIds: found ? { _id: found._id, id: found.id, tempId: found.tempId } : null,
      });
    } catch {}

    // If this message is still optimistic (no server _id yet), queue the delete to run once
    // the delivered message (with real _id) arrives via socket/newMessage, and optimistically
    // remove it from UI now. Optionally clean up attachments immediately by publicId.
    if (found && !found._id && found.tempId && !found.id) {
      try {
        console.debug('[conciergeStore] deleteMessage -> queue (no _id yet)', { key, tempId: found.tempId, hardDelete, deleteAttachments });
      } catch {}
      // Detailed debug log for queuing delete
      try {
        console.debug('[conciergeStore] QUEUE DELETE', {
          key,
          tempId: found.tempId,
          requestedMessageId: String(messageId),
          hasAttachments: Array.isArray(found.attachments) && found.attachments.length > 0,
          attachmentPublicIds: (found.attachments || []).map(a => a && a.publicId).filter(Boolean)
        });
      } catch {}

      set((state) => {
        const pendingMap = { ...(state.pendingDeletesByThread?.[key] || {}) };
        if (found.tempId) {
          pendingMap[found.tempId] = { hardDelete: !!hardDelete, deleteAttachments: !!deleteAttachments, at: Date.now() };
        }
        const curList = state.messagesByThread?.[key] || [];
        const nextList = curList.filter((m) => String(m.tempId || m.id) !== String(messageId));
        const curSel = new Set(state.selectedByThread?.[key] || []);
        curSel.delete(String(messageId));
        return {
          pendingDeletesByThread: { ...(state.pendingDeletesByThread || {}), [key]: pendingMap },
          messagesByThread: { ...(state.messagesByThread || {}), [key]: nextList },
          selectedByThread: { ...(state.selectedByThread || {}), [key]: Array.from(curSel) },
        };
      });

      // Best-effort: if requested, delete attachments now by publicId (publicId-only API path)
      if (deleteAttachments && Array.isArray(found.attachments) && found.attachments.length > 0) {
        // Log before cleanup
        console.debug('[conciergeStore] queued delete: starting attachment cleanup (publicId-only)', {
          key,
          count: found.attachments.length
        });
        try {
          const tasks = found.attachments
            .map(a => a && a.publicId)
            .filter(Boolean)
            .map(publicId => conciergeApi.deleteAttachment({ propertyId: pid, userUid: uidResolved, publicId }));
          if (tasks.length) {
            Promise.allSettled(tasks).then(r => console.debug('[conciergeStore] queued delete: attachment cleanup results', r));
          }
        } catch (eClean) {
          console.warn('[conciergeStore] queued delete: attachment cleanup failed (non-fatal)', eClean);
        }
      }
      // --- RESCUE TIMER: if socket delivery is delayed/missed, re-check soon ---
      setTimeout(() => {
        try { tryFlushQueuedDeletes(pid, uidResolved); } catch (e) { console.warn('[conciergeStore] rescue timer flush failed', e); }
      }, 2000);
      return;
    }
    else {
      try { console.debug('[conciergeStore] deleteMessage -> has stable id; will call API', { key, apiId, messageId }); } catch {}
    }

    // optimistic UI: mark as deleting for the matched message
    try {
      console.debug('[conciergeStore] deleteMessage optimistic mark deleting', apiId);
    } catch {}
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      list = list.map((m) => {
        const mid = String(m._id || m.id || m.tempId || '');
        return (mid === String(apiId) || mid === String(messageId)) ? { ...m, deleting: true } : m;
      });
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });

    const callApi = async (id) => conciergeApi.deleteMessage({ propertyId: pid, userUid: uidResolved, messageId: id, hardDelete, deleteAttachments });

    try {
      await callApi(apiId);
      get().applyMessageDeleted({ propertyId: pid, userUid: uidResolved, messageId: apiId, hardDelete });
    } catch (e) {
      try {
        console.error('[conciergeStore] deleteMessage API error', e);
      } catch {}
      const is404 = (e && (e.status === 404 || /404/.test(e.message || '')));
      if (is404 && altId && altId !== apiId) {
        try {
          console.debug('[conciergeStore] deleteMessage retry with altId', { apiId, altId });
        } catch {}
        console.warn('[conciergeStore] deleteMessage 404 – retrying with altId', { apiIdTried: apiId, altId });
        try {
          await callApi(altId);
          get().applyMessageDeleted({ propertyId: pid, userUid: uidResolved, messageId: altId, hardDelete });
          return;
        } catch (e2) {
          console.error('[conciergeStore] deleteMessage retry failed', e2);
        }
      }
      console.error('[conciergeStore] deleteMessage failed', e);
      // revert deleting flag on failure
      set((state) => {
        let list = [...(state.messagesByThread?.[key] || [])];
        list = list.map((m) => {
          const mid = String(m._id || m.id || m.tempId || '');
          return (mid === String(apiId) || mid === String(messageId)) ? { ...m, deleting: false, error: true } : m;
        });
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      });
    }
  },

  async deleteSelectedMessages({ propertyId, userUid, ids, hardDelete = false, deleteAttachments = true }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const st = get();
    const selected = Array.isArray(ids) && ids.length ? ids.map(String) : (st.selectedByThread?.[key] || []);
    for (const id of selected) {
      await get().deleteMessage({ propertyId: pid, userUid, messageId: id, hardDelete, deleteAttachments });
    }
    set((state) => ({
      selectedByThread: { ...(state.selectedByThread || {}), [key]: [] },
      selectionModeByThread: { ...(state.selectionModeByThread || {}), [key]: false },
      lastAnchorByThread: { ...(state.lastAnchorByThread || {}), [key]: null },
    }));
  },

  async deleteAttachment({ propertyId, userUid, messageId, publicId }) {
    const st = get();
    const pidResolved =
      propertyId ||
      (st.activeThread && st.activeThread.propertyId) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId'));
    const uidResolved = userUid || (st.activeThread && st.activeThread.userUid);

    if (!pidResolved || !uidResolved || !publicId) {
      console.error('[conciergeStore] deleteAttachment aborted: missing pid/userUid/publicId', {
        pidResolved,
        uidResolved,
        publicId,
      });
      return;
    }

    const pid = toIdString(pidResolved);
    const key = getThreadKey(pid, uidResolved);

    // Resolve the best server id to call with, avoiding tempId vs _id mismatch
    const list0 = (get().messagesByThread?.[key] || []);
    const found = list0.find(m => String(m._id || m.id || m.tempId) === String(messageId));
    const apiId = found && found._id ? String(found._id) : String(messageId);
    const altId = found
      ? (apiId === String(found._id) ? (found.id ? String(found.id) : null) : (found._id ? String(found._id) : null))
      : null;

    // --- DEBUG ---
    try {
      console.debug('[conciergeStore] deleteAttachment info', {
        threadKey: key,
        incomingMessageId: String(messageId),
        apiId,
        altId,
        publicId,
        foundExists: !!found,
        foundIds: found ? { _id: found._id, id: found.id, tempId: found.tempId } : null,
      });
    } catch {}

    // Optimistic remove with rollback snapshot
    let snapshot = null;
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      list = list.map((m) => {
        const mid = String(m._id || m.id || m.tempId || '');
        if (
          (apiId && mid === String(apiId)) ||
          (messageId && mid === String(messageId)) ||
          (!apiId && !messageId && mid)
        ) {
          const prev = (m.attachments || []);
          const next = prev.filter((a) => a?.publicId !== publicId);
          // Save snapshot for potential rollback
          snapshot = { messageId: mid || null, attachment: prev.find((a) => a?.publicId === publicId) || null };
          return { ...m, attachments: next, updating: true };
        }
        return m;
      });
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });

    // If we don't have a usable server id yet, immediately attempt publicId-only deletion
    if (!apiId && !altId && !messageId) {
      console.debug('[conciergeStore] deleteAttachment → publicId-only (no server id yet)', { publicId, key });
      try {
        await conciergeApi.deleteAttachment({ propertyId: pid, userUid: uidResolved, publicId });
        set((state) => {
          let list = [...(state.messagesByThread?.[key] || [])];
          list = list.map((m) => ({ ...m, updating: false, error: false }));
          return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
        });
        return;
      } catch (e0) {
        console.error('[conciergeStore] deleteAttachment publicId-only failed (no id)', e0);
        // Rollback
        set((state) => {
          let list = [...(state.messagesByThread?.[key] || [])];
          list = list.map((m) => {
            const restored = snapshot && snapshot.attachment
              ? [ ...(m.attachments || []), snapshot.attachment ]
              : (m.attachments || []);
            return { ...m, attachments: restored, updating: false, error: true };
          });
          return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
        });
        return;
      }
    }

    const callApi = async (id) => conciergeApi.deleteAttachment({ propertyId: pid, userUid: uidResolved, messageId: id, publicId });

    try {
      await callApi(apiId);
      // clear updating flag on success
      set((state) => {
        let list = [...(state.messagesByThread?.[key] || [])];
        list = list.map((m) => {
          const mid = String(m._id || m.id || m.tempId || '');
          return (mid === String(apiId) || mid === String(messageId)) ? { ...m, updating: false } : m;
        });
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      });
      return;
    } catch (e) {
      console.error('[conciergeStore] deleteAttachment failed', e);
      const is404 = (e && (e.status === 404 || /404/.test(e.message || '')));

      // Retry with altId if available
      if (is404 && altId && altId !== apiId) {
        console.warn('[conciergeStore] deleteAttachment 404 – retrying with altId', { apiIdTried: apiId, altId });
        try {
          await callApi(altId);
          set((state) => {
            let list = [...(state.messagesByThread?.[key] || [])];
            list = list.map((m) => {
              const mid = String(m._id || m.id || m.tempId || '');
              return (mid === String(altId)) ? { ...m, updating: false, error: false } : m;
            });
            return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
          });
          return;
        } catch (e2) {
          console.error('[conciergeStore] deleteAttachment retry (altId) failed', e2);
        }
      }

      // Last attempt: publicId-only (server supports this path; messageId omitted)
      try {
        await conciergeApi.deleteAttachment({ propertyId: pid, userUid: uidResolved, publicId });
        set((state) => {
          let list = [...(state.messagesByThread?.[key] || [])];
          list = list.map((m) => {
            const mid = String(m._id || m.id || m.tempId || '');
            return (mid === String(apiId) || mid === String(messageId)) ? { ...m, updating: false, error: false } : m;
          });
          return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
        });
        return;
      } catch (e3) {
        console.error('[conciergeStore] deleteAttachment publicId-only attempt failed', e3);
      }

      // Final failure: rollback the optimistic removal & mark error
      set((state) => {
        let list = [...(state.messagesByThread?.[key] || [])];
        list = list.map((m) => {
          const mid = String(m._id || m.id || m.tempId || '');
          if (mid === String(apiId) || mid === String(messageId)) {
            const restored = snapshot && snapshot.attachment
              ? [ ...(m.attachments || []), snapshot.attachment ]
              : (m.attachments || []);
            return { ...m, attachments: restored, updating: false, error: true };
          }
          return m;
        });
        return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
      });
    }
  },
  addTempMessage({ propertyId, userUid, message }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set((state) => {
      const list = state.messagesByThread?.[key] ? [...state.messagesByThread[key]] : [];
      // normalize and ensure an id
      const m = normalizeIncoming({ ...message });
      const id = m.id || m._id || m.tempId || `temp-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      list.push({ ...m, id, sending: true, replyTo: m.replyTo, replySnapshot: m.replySnapshot });
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });
  },

  replaceTempMessage({ propertyId, userUid, tempId, newMessage }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set((state) => {
      let list = [...(state.messagesByThread?.[key] || [])];
      const idx = list.findIndex((m) => m?.tempId === tempId);
      const incoming = normalizeIncoming(newMessage || {});
      const stableId = incoming._id || incoming.id || incoming.tempId;
      if (idx >= 0) {
        const prevReacts = Array.isArray(list[idx]?.reactions) ? list[idx].reactions.map(r => ({ emoji: r.emoji, count: Number(r.count)||0 })) : [];
        list[idx] = { ...incoming, sending: false, error: false, reactions: prevReacts };
      } else {
        list.push({ ...incoming, sending: false, error: false });
      }
      list = dedupeMessages(list);
      list = sortChronoAsc(list);

      // 🔧 If the temp message was selected, remap its id in the selection bucket
      const prevSel = state.selectedByThread?.[key] || [];
      const remappedSel = prevSel.map((sid) => (String(sid) === String(tempId) ? String(stableId) : sid));

      // 🧩 Apply any queued reactions for this message (arrived before server id)
      const pendingMap = state.pendingReactionsByThread?.[key] || {};
      const normAny = (v) => {
        const m = normalizeReactionsMap(v);
        return Object.entries(m).map(([emoji, ent]) => ({ emoji, count: Number(ent.count)||0 }));
      };
      // Merge any queued reactions recorded under the delivered id OR the temp id
      const qServer = pendingMap[String(stableId)];
      const qTemp   = pendingMap[String(tempId)];
      let mergedQueued = [];
      for (const bucket of [normAny(qServer), normAny(qTemp)]) {
        for (const r of bucket) mergedQueued = applyDeltaToReactions(mergedQueued, r.emoji, r.count);
      }
      if (mergedQueued.length) {
        const j = list.findIndex(m => String(m._id || m.id || m.tempId) === String(stableId));
        if (j !== -1) {
          const asMap = {};
          for (const r of mergedQueued) asMap[r.emoji] = { hotel:false, guest:false, count: Number(r.count)||0 };
          list[j] = { ...list[j], reactions: mapToArray(asMap), updating: false };
          try { console.debug('[conciergeStore] applied queued reactions (temp+server) after temp-&gt;delivered swap', { key, stableId: String(stableId), count: mergedQueued.length }); } catch {}
        }
      }

      const nextState = {
        messagesByThread: { ...(state.messagesByThread || {}), [key]: list },
        selectedByThread: { ...(state.selectedByThread || {}), [key]: remappedSel },
      };
      if (mergedQueued.length) {
        const nextPR = { ...(state.pendingReactionsByThread || {}) };
        const bucket = { ...(nextPR[key] || {}) };
        delete bucket[String(stableId)];
        delete bucket[String(tempId)];
        nextPR[key] = bucket;
        nextState.pendingReactionsByThread = nextPR;
        try { console.debug('[conciergeStore] cleared queued reactions bucket (replaceTempMessage)', { key, clearedKeys: [String(stableId), String(tempId)] }); } catch {}
      }
      return nextState;
    });
  },

  failTempMessage({ propertyId, userUid, tempId }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    set((state) => {
      const list = [...(state.messagesByThread?.[key] || [])];
      const idx = list.findIndex((m) => m?.tempId === tempId);
      if (idx >= 0) list[idx] = { ...list[idx], sending: false, error: true };
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: list } };
    });
  },
  // Retry a failed optimistic message by its tempId. Re-uploads blob attachments if needed.
  async retrySend({ propertyId, userUid, tempId }) {
    const pid = toIdString(propertyId);
    const key = getThreadKey(pid, userUid);
    const state0 = get();
    const list = state0.messagesByThread?.[key] || [];
    const msg = list.find((m) => m?.tempId === tempId);
    if (!msg) return;

    // mark as re-sending in UI
    set((state) => {
      const cur = [...(state.messagesByThread?.[key] || [])];
      const idx = cur.findIndex((m) => m?.tempId === tempId);
      if (idx >= 0) cur[idx] = { ...cur[idx], sending: true, error: false };
      return { messagesByThread: { ...(state.messagesByThread || {}), [key]: cur } };
    });

    try {
      // Prepare attachments: if they are blob/data URLs, re-upload; otherwise pass through
      const outAtts = [];
      for (const a of (msg.attachments || [])) {
        const u = a?.url || a?.thumbnailUrl;
        const isBlob = typeof u === 'string' && (u.startsWith('blob:') || u.startsWith('data:'));
        if (isBlob) {
          const resp = await fetch(u);
          const blob = await resp.blob();
          const filename = a?.name || `image-${Date.now()}.jpg`;
          const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
          const uploaded = await conciergeApi.uploadAttachment({ propertyId: pid, userUid, file });
          outAtts.push(uploaded);
        } else {
          outAtts.push(a);
        }
      }

      const content = (msg.content && String(msg.content).trim()) || (outAtts.length ? '(attachment)' : '');
      const me = get().me;
      const senderObj = me
        ? { id: me._id || me.id, role: 'staff', displayName: me.fullName || me.name || me.username }
        : { role: 'staff' };
      const res = await conciergeApi.sendMessage({ propertyId: pid, userUid, sender: senderObj, content, attachments: outAtts, tempId });
      const serverMsg = res?.message || res;
      get().replaceTempMessage({ propertyId: pid, userUid, tempId, newMessage: serverMsg });
    } catch (e) {
      console.warn('[conciergeStore] retrySend failed', e);
      get().failTempMessage({ propertyId: pid, userUid, tempId });
    }
  },
  // -----------------------------------------------------------------------
  async sendMessage(payload) {
    // payload: { propertyId, userUid, content, ... }
    const nowTempId = payload.tempId || `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    console.debug('[conciergeStore] sendMessage payload=', payload, 'tempId=', nowTempId);
    const { propertyId, userUid } = payload;
    const key = getThreadKey(propertyId, userUid);

    // If the composer already added an optimistic message with this tempId, don't add another.
    const state0 = get();
    const alreadyOptimistic = (state0.messagesByThread?.[key] || []).some(m => m.tempId === nowTempId && m.sending);
    if (!alreadyOptimistic) {
      const optimisticMsg = {
        ...payload,
        id: nowTempId,
        tempId: nowTempId,
        sending: true,
        createdAt: new Date().toISOString(),
        replyTo: payload.replyTo,
        replySnapshot: payload.replySnapshot,
      };
      set(state => ({
        messagesByThread: {
          ...state.messagesByThread,
          [key]: [...(state.messagesByThread[key] || []), optimisticMsg],
        },
        error: null,
      }));
    }
    try {
      // Always include tempId so the backend stores & echoes it; this lets us swap the exact optimistic bubble
      const realRes = await conciergeApi.sendMessage({ ...payload, tempId: nowTempId });
      try {
        const echoed = realRes?.message?.tempId ?? realRes?.tempId;
        if (String(echoed || '') !== String(nowTempId)) {
          console.warn('[conciergeStore] TEMP-ID MISMATCH (server did not echo)', { nowTempId, echoed });
        } else {
          console.debug('[conciergeStore] tempId echoed OK', { nowTempId });
        }
      } catch {}
      const delivered = withStableId(realRes?.message || realRes || {});
      set(state => {
        let list = state.messagesByThread[key] || [];
        const normDelivered = normalizeIncoming(delivered);
        // surface reply meta in delivered as well
        const deliveredWithReply = { ...normDelivered, replyTo: delivered.replyTo ?? normDelivered.replyTo, replySnapshot: delivered.replySnapshot ?? normDelivered.replySnapshot };
        const payloadText = payload.content ?? payload.text ?? '';
        const deliveredText = normDelivered.text ?? normDelivered.content ?? payloadText;

        // Primary: replace optimistic by tempId if both sides carry it
        const byTemp = (m) => m.tempId && deliveredWithReply.tempId && m.tempId === deliveredWithReply.tempId;
        // Fallback: replace the latest optimistic message with same sender + text
        const byContent = (m) => m.sending && sameSender(m.sender, payload.sender) && ((m.text ?? m.content ?? '') === deliveredText);
        let replaced = false;
        list = list.map(m => {
          if (byTemp(m) || (!deliveredWithReply.tempId && byContent(m))) {
            replaced = true;
            return { ...deliveredWithReply, sending: false, error: false };
          }
          return m;
        });

        // If nothing was replaced (e.g., no optimistic existed), append the delivered one once
        if (!replaced) {
          list = [...list, { ...deliveredWithReply, sending: false, error: false }];
        }

        // Final safety: de‑dupe by id/_id/tempId
        list = dedupeMessages(list);
        // Keep chronological order
        list = sortChronoAsc(list);

        // If reactions arrived before the delivered server id, apply them now
        const stableDeliveredId = deliveredWithReply._id || deliveredWithReply.id || deliveredWithReply.tempId;
        const pendingMap = state.pendingReactionsByThread?.[key] || {};
        const norm = (arr) => (Array.isArray(arr) ? arr.map(r => ({ emoji: r.emoji, count: Number(r.count) || 0 })) : []);
        const qServer = pendingMap[String(stableDeliveredId)];
        const qTemp   = pendingMap[String(nowTempId)];
        const serverSnapLen = norm(qServer).length;
        const tempSnapLen = norm(qTemp).length;

        // --- TEMP-ONLY REACTION MERGE DIAGNOSTIC ---
        if (serverSnapLen === 0 && tempSnapLen > 0) {
          const bannerKey = `${key}:${stableDeliveredId}`;
          if (!__warnedTempOnly.has(bannerKey)) {
            __warnedTempOnly.add(bannerKey);
            try {
              console.warn('\n================ TEMP-ONLY REACTION MERGE ================\n',
                { key, serverKey: stableDeliveredId, tempKey: nowTempId, note: 'Reaction(s) arrived under tempId before serverId; will merge & clear both buckets.' },
                '\n==========================================================\n');
            } catch {}
          }
        }
        // --- STRONGER DEBUG LOG ---
        try {
          console.debug('[conciergeStore] sendMessage queuedReacts merge DEBUG', {
            key,
            stableDeliveredId,
            nowTempId,
            serverSnapLen,
            tempSnapLen,
            pendingKeys: Object.keys(pendingMap)
          });
        } catch {}

        let queuedReacts = [];
        for (const bucket of [norm(qServer), norm(qTemp)]) {
          for (const r of bucket) queuedReacts = applyDeltaToReactions(queuedReacts, r.emoji, r.count);
        }
        if (!queuedReacts.length) {
          const anyKeys = Object.keys(pendingMap);
          if (anyKeys.length) {
            const appliedFromKey = anyKeys[0];
            queuedReacts = norm(pendingMap[appliedFromKey]);
            try { console.warn('[conciergeStore] sendMessage: applying queued reactions via fallback key', { key, stableDeliveredId: String(stableDeliveredId), appliedFromKey }); } catch {}
          }
        }
        if (queuedReacts.length) {
          const j = list.findIndex(m => String(m._id || m.id || m.tempId) === String(stableDeliveredId));
          if (j !== -1) {
            list[j] = { ...list[j], reactions: queuedReacts, updating: false };
          } else {
            // If message not found, schedule fallback apply as in handleNewMessage
            try {
              console.debug('[conciergeStore] sendMessage: message not found for applying queued reactions, scheduling fallback', { key, stableDeliveredId });
            } catch {}
            try { queueMicrotask(() => scheduleApplyQueuedReactions(key, String(stableDeliveredId))); }
            catch { setTimeout(() => scheduleApplyQueuedReactions(key, String(stableDeliveredId)), 0); }
          }
        }

        // 🔧 Remap selection if it contains the nowTempId to the delivered server id.
        const prevSelSend = state.selectedByThread?.[key] || [];
        const remappedSelSend = prevSelSend.map((sid) => (String(sid) === String(nowTempId) ? String(stableDeliveredId) : sid));

        const nextState = {
          messagesByThread: { ...state.messagesByThread, [key]: list },
          selectedByThread: { ...(state.selectedByThread || {}), [key]: remappedSelSend },
        };
        // Clear the queued reactions for this message now that they have been applied
        if (queuedReacts && queuedReacts.length) {
          const nextPR = { ...(state.pendingReactionsByThread || {}) };
          const bucket = { ...(nextPR[key] || {}) };
          delete bucket[String(stableDeliveredId)];
          delete bucket[String(nowTempId)];
          nextPR[key] = bucket;
          nextState.pendingReactionsByThread = nextPR;
          try { console.debug('[conciergeStore] cleared queued reactions bucket (sendMessage delivery)', { key, clearedKeys: [String(stableDeliveredId), String(nowTempId)] }); } catch {}
        }
        return nextState;
      });
      // Emit concierge:active after successful message send
      const actor = resolveCurrentActor(get);
      if (get().socket && actor && actor.role) {
        get().socket.emit('concierge:active', { propertyId, userUid, role: actor.role });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      set(state => ({
        error: err?.message || 'Failed to send message',
        messagesByThread: {
          ...state.messagesByThread,
          [key]: (state.messagesByThread[key] || []).map(msg =>
            msg.tempId === nowTempId ? { ...msg, sending: false, error: true } : msg
          ),
        },
      }));
    }
  },

  async markRead(propertyId, userUid) {
    console.debug('[conciergeStore] markRead propertyId=', propertyId, 'userUid=', userUid);
    try {
      const pid = propertyId || (typeof localStorage !== 'undefined' && localStorage.getItem('activePropertyId'));
      if (!pid || !userUid) return;
      await conciergeApi.markRead({ propertyId: pid, userUid });
      set((state) => ({
        threads: state.threads.map((t) =>
          samePropId(t.propertyId, pid) && t.userUid === userUid
            ? { ...t, unread: { ...(t.unread || {}), staff: 0 } }
            : t
        ),
      }));
    } catch (err) {
      console.error('Error marking as read:', err);
      set({ error: err?.message || 'Failed to mark as read' });
    }
  },

 handleNewMessage(msg) {
  let { propertyId, userUid } = msg || {};

  // Fallback to active thread if payload is missing ids
  if (!propertyId || !userUid) {
    try {
      const st = useConciergeStore.getState();
      if (st.activeThread?.propertyId && st.activeThread?.userUid) {
        propertyId = st.activeThread.propertyId;
        userUid = st.activeThread.userUid;
      }
    } catch {}
  }
  if (!propertyId || !userUid) return;

  const pid = toIdString(propertyId);
  const key = getThreadKey(pid, userUid);
  const st = useConciergeStore.getState();
  const isActiveRoom = !!(st.activeThread && samePropId(st.activeThread.propertyId, pid) && st.activeThread.userUid === userUid);

  const incoming = normalizeIncoming(msg);
  if (incoming.replyTo || incoming.replySnapshot) {
    try { console.debug('[conciergeStore] incoming has reply meta', { id: incoming.id || incoming._id, replyTo: incoming.replyTo, hasSnapshot: !!incoming.replySnapshot }); } catch {}
  }

  set((state) => {
    let list = state.messagesByThread[key] || [];

    // --- Reconcile with any optimistic message ---
    const byTempIdx = list.findIndex(m => m.tempId && incoming.tempId && String(m.tempId) === String(incoming.tempId));
    const incomingText = incoming.text ?? incoming.content ?? '';
    const byContentIdx = (byTempIdx === -1)
  ? list.findIndex(m => m.sending && sameSender(m.sender, incoming.sender) && ((m.text ?? m.content ?? '') === incomingText))
  : -1;
    const byServerIdx = list.findIndex(m => (m._id && incoming._id && String(m._id) === String(incoming._id)) || (m.id && incoming.id && String(m.id) === String(incoming.id)));

    if (byTempIdx !== -1 || byContentIdx !== -1) {
      const idx = (byTempIdx !== -1) ? byTempIdx : byContentIdx;
      list = [...list];
      list[idx] = { ...incoming, sending: false, error: false, replyTo: incoming.replyTo, replySnapshot: incoming.replySnapshot };
    } else if (byServerIdx === -1) {
      list = [...list, { ...incoming, replyTo: incoming.replyTo, replySnapshot: incoming.replySnapshot }];
    }

    // --- Apply any queued reactions that arrived before delivery ---
    const deliveredIdFinal = String(incoming._id || incoming.id || '');
    let nextPendingMapForReturn = null; // ensure we RETURN this instead of mutating state

    if (deliveredIdFinal) {
      const pendingMap = state.pendingReactionsByThread?.[key] || {};
      const serverKey = deliveredIdFinal;
      const tempKey = String(incoming.tempId || '');
      const normAny = (v) => {
        const m = normalizeReactionsMap(v);
        return Object.entries(m).map(([emoji, ent]) => ({ emoji, count: Number(ent.count)||0 }));
      };

      const serverSnap = normAny(pendingMap[serverKey]);
      const tempSnap   = normAny(pendingMap[tempKey]);

      try {
        console.debug('[conciergeStore] handleNewMessage pendingReactions snapshot', {
          key,
          deliveredIdFinal,
          serverKey,
          tempKey,
          keys: Object.keys(pendingMap),
          serverSnapLen: serverSnap.length,
          tempSnapLen: tempSnap.length,
        });
      } catch {}

      // One-time banner when merging from temp-only
      if (serverSnap.length === 0 && tempSnap.length > 0) {
        const bannerKey = `${key}:${serverKey}`;
        if (!__warnedTempOnly.has(bannerKey)) {
          __warnedTempOnly.add(bannerKey);
          try {
            console.warn('\n================ TEMP-ONLY REACTION MERGE ================\n',
              { key, serverKey, tempKey, note: 'Reaction(s) arrived under tempId before serverId; will merge & clear both buckets.' },
              '\n==========================================================\n');
          } catch {}
        }
      }

      // Merge both buckets deterministically: server first, then temp
      let merged = [];
      for (const r of serverSnap) merged = applyDeltaToReactions(merged, r.emoji, r.count);
      for (const r of tempSnap)   merged = applyDeltaToReactions(merged, r.emoji, r.count);

      // Fallback: if nothing found under either key, try any available bucket
      if (!merged.length) {
        const anyKeys = Object.keys(pendingMap);
        if (anyKeys.length) {
          const appliedFromKey = anyKeys[0];
          for (const r of normAny(pendingMap[appliedFromKey])) merged = applyDeltaToReactions(merged, r.emoji, r.count);
          try { console.warn('[conciergeStore] handleNewMessage: applying queued reactions via fallback key', { key, deliveredIdFinal, appliedFromKey }); } catch {}
        }
      }

      if (merged.length) {
        try {
          console.debug('[conciergeStore] handleNewMessage will apply merged reactions', {
            key,
            deliveredIdFinal,
            fromServerBucket: serverSnap.length,
            fromTempBucket: tempSnap.length,
            merged,
          });
        } catch {}

        const j = list.findIndex(m => String(m._id || m.id || m.tempId) === serverKey);
        if (j !== -1) {
          const asMap = {};
          for (const r of merged) asMap[r.emoji] = { hotel:false, guest:false, count: Number(r.count)||0 };
          list[j] = { ...list[j], reactions: mapToArray(asMap), updating: false };
          try { console.debug('[conciergeStore] applied queued reactions on handleNewMessage', { key, deliveredIdFinal, count: merged.length }); } catch {}
        } else {
          // message not yet present; schedule a retry
          try { queueMicrotask(() => scheduleApplyQueuedReactions(key, serverKey)); } catch { setTimeout(() => scheduleApplyQueuedReactions(key, serverKey), 0); }
        }

        // Clear both buckets AFTER applying — persist via returned state
        const nextPR = { ...(state.pendingReactionsByThread || {}) };
        const bucket = { ...(nextPR[key] || {}) };
        delete bucket[serverKey];
        if (tempKey) delete bucket[tempKey];
        nextPR[key] = bucket;
        nextPendingMapForReturn = nextPR;
        try { console.debug('[conciergeStore] cleared queued reactions bucket (handleNewMessage)', { key, clearedKeys: [serverKey, tempKey].filter(Boolean), serverSnapLen: serverSnap.length, tempSnapLen: tempSnap.length }); } catch {}
      }
    }

    // Keep list ordered
    list = sortChronoAsc(list);

    // Update thread preview & unread
    const isActive = !!(state.activeThread && samePropId(state.activeThread.propertyId, pid) && state.activeThread.userUid === userUid);
    const updatedThreads = (state.threads || []).map((t) => {
      if (samePropId(t.propertyId, pid) && t.userUid === userUid) {
        const lastMessage = incoming.content || incoming.text || t.lastMessage || '';
        const lastAt = new Date(incoming.timestamp || incoming.createdAt || Date.now()).toISOString();
        const unread = {
          ...(t.unread || {}),
          staff: isActive ? 0 : (t.unread?.staff || 0) + (senderRoleOf(incoming) === 'guest' ? 1 : 0),
        };
        return { ...t, lastMessage, lastAt, unread };
      }
      return t;
    });
    const sortedThreads = [...updatedThreads].sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''));

    const out = { messagesByThread: { ...state.messagesByThread, [key]: list }, threads: sortedThreads };
    if (nextPendingMapForReturn) out.pendingReactionsByThread = nextPendingMapForReturn;
    return out;
  });

  // --- After-state side effects ---
  // If a delete was queued while this message had only tempId, execute it now using the delivered _id
  try {
    const stNow = useConciergeStore.getState();
    const queuedMap = (stNow.pendingDeletesByThread?.[key] || {});
    const tId = incoming.tempId;
    const deliveredId = incoming._id || incoming.id;
    try { console.debug('[conciergeStore] CHECK QUEUED DELETE', { key, tempId: tId, deliveredId, queuedTempIds: Object.keys(queuedMap) }); } catch {}
    if (tId && queuedMap[tId] && deliveredId) {
      const { hardDelete, deleteAttachments } = queuedMap[tId];
      set((state) => {
        const m = { ...(state.pendingDeletesByThread?.[key] || {}) };
        delete m[tId];
        return { pendingDeletesByThread: { ...(state.pendingDeletesByThread || {}), [key]: m } };
      });
      try { console.debug('[conciergeStore] EXECUTE QUEUED DELETE', { key, tempId: tId, deliveredId, hardDelete, deleteAttachments }); } catch {}
      useConciergeStore.getState().deleteMessage({ propertyId: pid, userUid, messageId: deliveredId, hardDelete, deleteAttachments });
    }
  } catch (eQD) {
    try { console.warn('[conciergeStore] handleNewMessage queued-delete check failed', eQD); } catch {}
  }

  // Mark read/seen when room is active and incoming is from guest
  if (isActiveRoom && incoming && incoming.sender === 'guest') {
    try { useConciergeStore.getState().markRead(pid, userUid); } catch (e) { try { console.warn('[conciergeStore] markRead (post-newMessage) failed', e); } catch {} }
    try {
      const mid = incoming._id || incoming.id;
      if (mid && conciergeApi.seenMessage) {
        conciergeApi.seenMessage({ propertyId: pid, userUid, messageId: mid }).catch(() => {});
      }
    } catch {}
  }
},

  handleMessageSeen({ propertyId, userUid, messageId }) {
    console.debug('[conciergeStore] handleMessageSeen', { propertyId, userUid, messageId });
    const key = getThreadKey(propertyId, userUid);
    set(state => ({
      messagesByThread: {
        ...state.messagesByThread,
        [key]: (state.messagesByThread[key] || []).map(msg =>
          ((msg.id === messageId) || (msg._id === messageId)) ? { ...msg, seen: true } : msg
        ),
      },
    }));
  },
}));

if (typeof window !== 'undefined') {
  try {
    window.__conciergeDebug = {
      dumpThread(pid, uid) {
        const key = `${pid}:${uid}`;
        const state = useConciergeStore.getState();
        const list = state.messagesByThread[key] || [];
        console.log('[conciergeDebug] dumpThread key=', key, 'len=', list.length, 'sample first=', pickSample(list, 2).map(fmtMsg), 'sample last=', pickSample(list.slice(-2), 2).map(fmtMsg));
        return list;
      },
      findById(pid, uid, id) {
        const key = `${pid}:${uid}`;
        const list = useConciergeStore.getState().messagesByThread[key] || [];
        return list.find(m => (m.id || m._id) === id);
      },
      store: useConciergeStore,
      forceFlushQueuedDeletes(pid, uid) {
        tryFlushQueuedDeletes(String(pid), String(uid));
      },
      react(pid, uid, mid, emoji, delta = 1) {
        return useConciergeStore.getState().reactToMessage({ propertyId: pid, userUid: uid, messageId: mid, emoji, delta });
      },
    };
    console.debug('[conciergeStore] window.__conciergeDebug attached');
  } catch {}
}

export function ensureConciergeSocket(baseUrl) {
  const { initSocket } = useConciergeStore.getState();
  return initSocket(baseUrl);
}

// Mark a specific message as seen (optimistic; reconciles via socket/API)
export async function markSeen({ propertyId, userUid, messageId, role = 'staff' }) {
  const pid = toIdString(propertyId);
  if (!pid || !userUid || !messageId) return;
  // optimistic apply
  useConciergeStore.getState().applySeen({ propertyId: pid, userUid, messageId, role, at: new Date().toISOString() });
  // include actor id if available
  const actor = resolveCurrentActor(useConciergeStore.getState);
  try {
    await conciergeApi.markSeen({ propertyId: pid, userUid, messageId, actor: { role, id: actor?.id } });
  } catch (e) {
    console.warn('[conciergeStore] markSeen API failed (non-fatal; socket may still reconcile)', e);
  }
}

// Create an IntersectionObserver to auto-mark the latest incoming message as seen when visible.
// Usage from a component:
//   const detach = attachSeenObserver({ propertyId, userUid, myRole: 'staff', element: someDomNode, messageId: '...' });
//   // Cleanup: detach && detach();
export function attachSeenObserver({ propertyId, userUid, myRole = 'staff', element, messageId, threshold = 0.6 }) {
  try {
    if (typeof window === 'undefined' || !element || !messageId) return () => {};
    const pid = toIdString(propertyId);
    const mark = throttle(() => {
      markSeen({ propertyId: pid, userUid, messageId, role: myRole });
    }, 900);
    const io = new IntersectionObserver((entries) => {
      for (const ent of entries) {
        if (ent.isIntersecting && ent.intersectionRatio >= threshold) {
          mark();
          // one-shot by default; disconnect after first fire
          try { io.disconnect(); } catch {}
          return;
        }
      }
    }, { root: null, rootMargin: '0px', threshold: [threshold] });
    io.observe(element);
    return () => { try { io.disconnect(); } catch {} };
  } catch (e) {
    console.warn('[conciergeStore] attachSeenObserver failed', e);
    return () => {};
  }
}

// Convenience: find the last incoming (guest) bubble in DOM and observe it for auto-seen.
// You must have added data attributes on bubbles: data-role and data-message-id.
// Optionally pass a custom selector string to refine the container or bubble match.
export function autoMarkSeenLatest({ propertyId, userUid, myRole = 'staff', containerSelector }) {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
    const pid = toIdString(propertyId);
    const container = containerSelector ? document.querySelector(containerSelector) : document;
    if (!container) return () => {};
    // We want the last bubble from the opposite role (incoming for me)
    const otherRole = myRole === 'staff' ? 'guest' : 'staff';
    const nodes = container.querySelectorAll(`[data-role="${otherRole}"][data-message-id]`);
    if (!nodes || !nodes.length) return () => {};
    const target = nodes[nodes.length - 1];
    const messageId = target.getAttribute('data-message-id');
    if (!messageId) return () => {};
    return attachSeenObserver({ propertyId: pid, userUid, myRole, element: target, messageId });
  } catch (e) {
    console.warn('[conciergeStore] autoMarkSeenLatest failed', e);
    return () => {};
  }
}
